import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Card, Badge, Form, Row, Col, Modal, ButtonGroup, InputGroup, Tabs, Tab, Spinner, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getClients, getTrips, apiClient } from '../api';
import { format } from 'date-fns';
import { FileText, Download, CheckCircle, CreditCard, DollarSign, Filter, Trash2, User, Search, AlertTriangle, FileSpreadsheet, File as FilePdf, Layers, Plus, ChevronDown, ChevronRight, History, Clock, RotateCcw, Edit, Save, RefreshCw } from 'lucide-react';
import SearchableSelect from './SearchableSelect';

const ClientBillingView = () => {
    const [clients, setClients] = useState([]);
    const [trips, setTrips] = useState([]);
    const [particularTrips, setParticularTrips] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [activeTab, setActiveTab] = useState('corporativo');
    
    // Filtros Empresa
    const [clientAreas, setClientAreas] = useState([]);
    const [clientCCs, setClientCCs] = useState([]);
    const [selectedClient, setSelectedClient] = useState('');
    const [selectedArea, setSelectedArea] = useState('');
    const [selectedCC, setSelectedCC] = useState('');
    const [selectedFile, setSelectedFile] = useState('');
    
    // Filtros de Seguimiento de Facturas
    const [trackStartDate, setTrackStartDate] = useState('');
    const [trackEndDate, setTrackEndDate] = useState('');
    const [trackClient, setTrackClient] = useState('');
    const [trackFolio, setTrackFolio] = useState('');
    const [trackPreInvoice, setTrackPreInvoice] = useState('');
    const [trackArea, setTrackArea] = useState('');
    
    // Modo Masivo
    const [isBulkMode, setIsBulkMode] = useState(false);
    const [bulkFilterType, setBulkFilterType] = useState('masivos'); // 'masivos' | 'no_masivos'
    const [bulkPreview, setBulkPreview] = useState([]);
    const [selectedBulkClients, setSelectedBulkClients] = useState([]);
    const [expandedBulkClients, setExpandedBulkClients] = useState([]);
    
    // Filtros Comunes
    const [expenseNature, setExpenseNature] = useState('');
    const [externalRef, setExternalRef] = useState('');
    const [passengerName, setPassengerName] = useState('');
    const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    
    // Filtros Específicos Particulares
    const [searchId, setSearchId] = useState('');
    const [searchPassenger, setSearchPassenger] = useState('');
    const [searchRequester, setSearchRequester] = useState('');
    const [searchFlowStatus, setSearchFlowStatus] = useState('');

    const [selectedTripIds, setSelectedTripsIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [particularLoading, setParticularLoading] = useState(false);

    // Modo Servicios Futuros y Modal Asignación OC
    const [isFutureMode, setIsFutureMode] = useState(false);
    const [showAssignOCModal, setShowAssignOCModal] = useState(false);
    const [assignOCNumber, setAssignOCNumber] = useState('');
    const [assignOCNotes, setAssignOCNotes] = useState('');
    const [submittingOC, setSubmittingOC] = useState(false);

    // Gestor de OC Anticipadas y Asociación de Facturas SII
    const [showOCManagerModal, setShowOCManagerModal] = useState(false);
    const [ocSummaryList, setOcSummaryList] = useState([]);
    const [loadingOCSummary, setLoadingOCSummary] = useState(false);
    const [expandedOCs, setExpandedOCs] = useState([]);
    const [selectedOCForInvoice, setSelectedOCForInvoice] = useState(null);
    const [showAttachInvoiceModal, setShowAttachInvoiceModal] = useState(false);
    const [attachFolio, setAttachFolio] = useState('');
    const [attachDate, setAttachDate] = useState(new Date().toISOString().split('T')[0]);
    const [submittingAttachInvoice, setSubmittingAttachInvoice] = useState(false);

    const loadOCSummary = async () => {
        setLoadingOCSummary(true);
        try {
            const res = await apiClient.get('/transport/billing/oc-summary');
            setOcSummaryList(res.data || []);
        } catch (e) {
            console.error(e);
        }
        setLoadingOCSummary(false);
    };

    const handleAttachInvoiceToOC = async () => {
        if (!attachFolio.trim() || !selectedOCForInvoice) return;
        setSubmittingAttachInvoice(true);
        try {
            await apiClient.post('/transport/billing/oc-attach-invoice', {
                oc_number: selectedOCForInvoice.oc_number,
                folio: attachFolio.trim(),
                date: attachDate || undefined
            });
            alert(`✅ Factura SII #${attachFolio.trim()} asociada exitosamente a la OC #${selectedOCForInvoice.oc_number} y actualizada en el Flujo de Caja.`);
            setShowAttachInvoiceModal(false);
            setAttachFolio('');
            setSelectedOCForInvoice(null);
            loadOCSummary();
        } catch (e) {
            console.error(e);
            alert(`⚠️ Error al asociar factura: ${e.response?.data?.detail || e.message}`);
        }
        setSubmittingAttachInvoice(false);
    };

    const handleDeleteOC = async (ocNumber) => {
        if (!window.confirm(`⚠️ ¿Estás seguro de eliminar la Orden de Compra #${ocNumber}?\n\n- Se desvinculará el N° de OC de todas las reservas asociadas.\n- Se eliminará la transacción por cobrar del Flujo de Caja.`)) {
            return;
        }
        try {
            await apiClient.delete(`/transport/billing/oc/${ocNumber}`);
            alert(`✅ Orden de Compra #${ocNumber} eliminada con éxito.`);
            loadOCSummary();
            loadPendingData();
        } catch (e) {
            console.error(e);
            alert(`⚠️ Error al eliminar la OC: ${e.response?.data?.detail || e.message}`);
        }
    };

    // Modales
    const [showPreInvoiceModal, setShowPreInvoiceModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [showPayModal, setShowPayModal] = useState(false);
    const [showDocManagerModal, setShowDocManagerModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [invoiceLogs, setInvoiceLogs] = useState([]);
    const [selectedInvoiceForLogs, setSelectedInvoiceForLogs] = useState(null);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [activeInvoice, setActiveInvoice] = useState(null);
    const [activeTrip, setActiveTrip] = useState(null); 
    const [activeDocument, setActiveDocument] = useState(null);
    const [payMethod, setPayMethod] = useState('TRANSFER');
    const [webpayCommission, setWebpayCommission] = useState(0);
    const [pendingCommission, setPendingCommission] = useState(false);

    // Nuevo Documento Form
    const [newDocType, setNewDocType] = useState('FACTURA');
    const [newDocFolio, setNewDocFolio] = useState('');
    const [newDocOC, setNewDocOC] = useState('');
    const [newDocHES, setNewDocHES] = useState('');
    const [newDocAmount, setNewDocAmount] = useState(0);

    const [editingDocId, setEditingDocId] = useState(null);
    const [editFolio, setEditFolio] = useState('');
    const [editOC, setEditOC] = useState('');
    const [editHES, setEditHES] = useState('');
    const [editAmount, setEditAmount] = useState('');
    const [editStatus, setEditStatus] = useState('pending');

    const [showDeleteDocModal, setShowDeleteDocModal] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);
    const [isDeletingDoc, setIsDeletingDoc] = useState(false);
    const [isUnpayingDoc, setIsUnpayingDoc] = useState(false);

    const [loadingInvoicesList, setLoadingInvoicesList] = useState(false);

    // Estados Conciliación Booking.com
    const [bookingFile, setBookingFile] = useState(null);
    const [reconciliationData, setReconciliationData] = useState(null);
    const [isReconciling, setIsReconciling] = useState(false);
    const [isApplyingAdjustments, setIsApplyingAdjustments] = useState(false);
    const [reconActiveSubTab, setReconActiveSubTab] = useState('matched');

    // 1. Cargar Maestros Iniciales bajo demanda
    const loadInvoices = async (force = false) => {
        const hasFilters = trackFolio.trim() || trackPreInvoice.trim() || trackClient || trackStartDate || trackEndDate || trackArea.trim();
        if (!force && !hasFilters) {
            setInvoices([]);
            return;
        }

        setLoadingInvoicesList(true);
        try {
            const params = new URLSearchParams();
            params.append('limit', '500');
            if (trackPreInvoice.trim()) params.append('pre_invoice_number', trackPreInvoice.trim());
            if (trackFolio.trim()) params.append('folio', trackFolio.trim());
            if (trackClient) params.append('client_id', trackClient);
            if (trackStartDate) params.append('start_date', trackStartDate);
            if (trackEndDate) params.append('end_date', trackEndDate);
            if (trackArea.trim()) params.append('area', trackArea.trim());

            const inv = await apiClient.get(`transport/invoices/?${params.toString()}`);
            setInvoices(inv.data || []);
        } catch (e) { 
            console.error("Error al buscar facturas/pre-facturas:", e); 
            alert("Error al buscar facturas o pre-facturas: " + (e.response?.data?.detail || e.message));
        } finally {
            setLoadingInvoicesList(false);
        }
    };

    const handleClearFilters = () => {
        setTrackFolio('');
        setTrackPreInvoice('');
        setTrackClient('');
        setTrackArea('');
        setTrackStartDate('');
        setTrackEndDate('');
        setInvoices([]);
    };

    const handleSaveEditDoc = async (docId) => {
        try {
            const payload = {
                folio: editFolio.trim() || null,
                oc_number: editOC.trim() || null,
                hes_number: editHES.trim() || null,
                amount: editAmount ? parseFloat(editAmount) : undefined,
                status: editStatus || undefined,
                date: new Date().toISOString().split('T')[0]
            };
            const res = await apiClient.put(`transport/invoices/documents/${docId}`, payload);
            const currentInv = res.data;
            setActiveInvoice(currentInv);
            setInvoices(prev => prev.map(inv => inv.id === currentInv.id ? currentInv : inv));
            setEditingDocId(null);
            alert("✅ Documento y Flujo de Transacciones actualizados exitosamente.");
        } catch (e) {
            console.error(e);
            alert("Error al actualizar el documento: " + (e.response?.data?.detail || e.message));
        }
    };

    const handleUnpayDoc = async (doc) => {
        if (!window.confirm(`¿Desea revertir el pago del documento ${doc.type} ${doc.folio ? '#' + doc.folio : ''}? El estado volverá a PENDIENTE.`)) {
            return;
        }
        setIsUnpayingDoc(true);
        try {
            const res = await apiClient.put(`transport/invoices/documents/${doc.id}/unpay`);
            const currentInv = res.data;
            setActiveInvoice(currentInv);
            setInvoices(prev => prev.map(inv => inv.id === currentInv.id ? currentInv : inv));
            alert("✅ Pago revertido exitosamente a PENDIENTE.");
        } catch (e) {
            console.error(e);
            alert("Error al revertir el pago: " + (e.response?.data?.detail || e.message));
        } finally {
            setIsUnpayingDoc(false);
        }
    };

    const handleConfirmDeleteDoc = (doc) => {
        setDocToDelete(doc);
        setShowDeleteDocModal(true);
    };

    const executeDeleteDoc = async () => {
        if (!docToDelete) return;
        setIsDeletingDoc(true);
        try {
            const res = await apiClient.delete(`transport/invoices/documents/${docToDelete.id}`);
            const currentInv = res.data;
            setActiveInvoice(currentInv);
            setInvoices(prev => prev.map(inv => inv.id === currentInv.id ? currentInv : inv));
            setShowDeleteDocModal(false);
            setDocToDelete(null);
            alert("✅ Documento eliminado de la prefactura. El saldo restante ha sido recalculado exitosamente.");
        } catch (e) {
            console.error(e);
            alert("Error al eliminar el documento: " + (e.response?.data?.detail || e.message));
        } finally {
            setIsDeletingDoc(false);
        }
    };

    useEffect(() => {
        const loadInitial = async () => {
            try {
                const [c] = await Promise.all([getClients()]);
                setClients(c.filter(cl => cl.name !== 'PARTICULARES SIN CONVENIO'));
                setInvoices([]);
            } catch (e) { console.error("Error loading initial data", e); }
        };
        loadInitial();
    }, []);

    // 2. Cargar Sub-Maestros (Áreas y CC) al cambiar cliente
    useEffect(() => {
        const loadSubMaestros = async () => {
            if (!selectedClient || isBulkMode) {
                setClientAreas([]); setClientCCs([]);
                setSelectedArea(''); setSelectedCC('');
                return;
            }
            try {
                const [areas, ccs] = await Promise.all([
                    apiClient.get(`transport/areas/?client_id=${selectedClient}`),
                    apiClient.get(`transport/cost-centers/?client_id=${selectedClient}`)
                ]);
                setClientAreas(areas.data);
                setClientCCs(ccs.data);
            } catch (e) { console.error(e); }
        };
        loadSubMaestros();
    }, [selectedClient, isBulkMode]);

    // 3. Cargar Viajes Empresa (Individual o Masivo / No Masivo)
    const loadPendingData = useCallback(async () => {
        setLoading(true);
        try {
            if (isBulkMode) {
                const isBulkParam = bulkFilterType === 'masivos';
                const res = await apiClient.get(`transport/invoices/bulk-preview?start_date=${startDate}&end_date=${endDate}&is_bulk=${isBulkParam}`);
                setBulkPreview(res.data);
                setSelectedBulkClients(res.data.map(r => r.client_id));
            } else {
                if (!selectedClient) { setLoading(false); return; }
                setSelectedFile('');

                const tripParams = {
                    client_id: selectedClient,
                    limit: 1000,
                    area_id: selectedArea || undefined,
                    cost_center_id: selectedCC || undefined,
                    expense_nature: expenseNature || undefined,
                    external_reference: externalRef || undefined,
                    passenger_name: passengerName || undefined
                };

                if (isFutureMode) {
                    // Modo Servicios Futuros: viajes no cancelados y aún no asociados a prefactura
                    tripParams.status = 'pending,assigned,en_route,arrived,on_board,completed';
                } else {
                    // Modo Estándar: viajes procesados listos para pre-facturar
                    tripParams.status = 'completed,cancelled_paid,no_show';
                    tripParams.admin_status = 'processed,settled';
                    tripParams.start_date = startDate + 'T00:00:00';
                    tripParams.end_date = endDate + 'T23:59:59';
                }

                const data = await getTrips(tripParams);
                setTrips(data.filter(t => !t.client_invoice_id));
            }
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [isBulkMode, bulkFilterType, isFutureMode, selectedClient, selectedArea, selectedCC, expenseNature, externalRef, passengerName, startDate, endDate]);

    const handleAssignOC = async () => {
        if (!assignOCNumber.trim()) {
            alert("Por favor ingresa un número de Orden de Compra (OC).");
            return;
        }
        const cleanOC = assignOCNumber.trim();
        setSubmittingOC(true);
        try {
            await apiClient.post('/transport/billing/assign-oc', {
                trip_ids: selectedTripIds,
                oc_number: cleanOC,
                notes: assignOCNotes.trim() || undefined
            });

            // Actualizar el estado local 'trips' DE INMEDIATO para que el badge y resalte amarillo aparezcan al instante en la UI
            setTrips(prevTrips => prevTrips.map(tr => {
                if (selectedTripIds.includes(tr.id)) {
                    return { ...tr, oc_number: cleanOC };
                }
                return tr;
            }));

            alert(`✅ OC #${cleanOC} asignada correctamente a los ${selectedTripIds.length} servicio(s) y registrada en el Flujo de Caja.`);
            setShowAssignOCModal(false);
            setAssignOCNumber('');
            setAssignOCNotes('');
            setSelectedTripsIds([]);
            loadPendingData();
        } catch (e) {
            console.error(e);
            alert(`⚠️ Error al asignar OC: ${e.response?.data?.detail || e.message}`);
        }
        setSubmittingOC(false);
    };

    // 4. Cargar Viajes Particulares
    const loadParticularTrips = useCallback(async () => {
        setParticularLoading(true);
        try {
            const allClients = await getClients();
            // Buscar todos los clientes cuya segmentación sea 'Particulares' o cuyo nombre contenga 'PARTICULAR'
            const particularClients = (allClients || []).filter(c => 
                (c.segmentation && c.segmentation.toLowerCase() === 'particulares') ||
                (c.name && c.name.toUpperCase().includes('PARTICULAR'))
            );
            
            const particularClientIds = particularClients.map(c => c.id);

            const queryParams = {
                search_term: searchId || undefined,
                passenger_name: searchPassenger || undefined,
                admin_status: searchFlowStatus || undefined,
                status: 'completed,cancelled_paid,no_show',
                start_date: startDate + 'T00:00:00',
                end_date: endDate + 'T23:59:59',
                client_segmentation: 'Particulares'
            };

            // Si existen clientes particulares identificados, pasamos la lista de IDs explícita
            if (particularClientIds.length > 0) {
                queryParams.client_id = particularClientIds.join(',');
            }

            const data = await getTrips(queryParams);
            
            // Filtro local adicional de seguridad: asegurar que ningún viaje pertenezca a un cliente corporativo o ajeno a Particulares
            const safeData = (data || []).filter(t => {
                if (!t.client) return false; // Si no tiene cliente o es desconocido, no pertenece a particulares
                const seg = (t.client.segmentation || '').toLowerCase();
                const name = (t.client.name || '').toUpperCase();
                return seg === 'particulares' || name.includes('PARTICULAR');
            });

            // Filtro local manual para solicitante para asegurar match (usando full_name según schema)
            let filtered = safeData;
            if (searchRequester) {
                filtered = safeData.filter(t => 
                    t.requester?.full_name?.toLowerCase().includes(searchRequester.toLowerCase()) ||
                    t.requester?.email?.toLowerCase().includes(searchRequester.toLowerCase())
                );
            }
            setParticularTrips(filtered);
        } catch (e) { console.error(e); }
        setParticularLoading(false);
    }, [startDate, endDate, searchId, searchPassenger, searchRequester, searchFlowStatus]);

    useEffect(() => {
        if (activeTab === 'corporativo') loadPendingData();
        else loadParticularTrips();
    }, [loadPendingData, loadParticularTrips, activeTab]);

    // Opciones de archivos y filtrado local (normalizado con trim)
    const fileOptions = React.useMemo(() => {
        const unique = [...new Set(trips.map(t => (t.file || '').trim()).filter(Boolean))];
        return unique.map(f => ({ id: f, name: f }));
    }, [trips]);

    const filteredTrips = React.useMemo(() => {
        if (!selectedFile) return trips;
        const normalizedSelected = selectedFile.trim();
        return trips.filter(t => (t.file || '').trim() === normalizedSelected);
    }, [trips, selectedFile]);

    // Handlers Corporativos
    const toggleTrip = (id) => setSelectedTripsIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    const selectAll = (e) => setSelectedTripsIds(e.target.checked ? filteredTrips.map(t => t.id) : []);

    const toggleBulkRow = (clientId) => {
        setExpandedBulkClients(prev => prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]);
    };

    const handleCreatePreInvoice = async () => {
        if (selectedTripIds.length === 0) return;
        setLoading(true);
        try {
            await apiClient.post('transport/invoices/pre-invoice', {
                client_id: parseInt(selectedClient),
                trip_ids: selectedTripIds,
                billing_period_label: `Cobro ${format(new Date(), 'MMM yyyy')}`,
                grouping_type: 'general'
            });
            setShowPreInvoiceModal(false); setSelectedTripsIds([]);
            await loadPendingData();
            loadInvoices(true);
        } catch (e) { alert("Error"); } finally { setLoading(false); }
    };

    // Funciones Conciliación Booking.com
    const handleBookingFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setBookingFile(e.target.files[0]);
        }
    };

    const handleUploadBookingReport = async () => {
        if (!bookingFile) return;
        setIsReconciling(true);
        setReconciliationData(null);
        try {
            const formData = new FormData();
            formData.append('file', bookingFile);
            
            const res = await apiClient.post('/transport/booking/reconcile', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            setReconciliationData(res.data);
            if (res.data.summary.discrepancies_count > 0) {
                setReconActiveSubTab('discrepancies');
            } else {
                setReconActiveSubTab('matched');
            }
        } catch (err) {
            alert("Error al conciliar reporte: " + (err.response?.data?.detail || err.message));
        } finally {
            setIsReconciling(false);
        }
    };

    const handleApplyBookingAdjustments = async (tripsToAdjust) => {
        if (!tripsToAdjust || tripsToAdjust.length === 0) return;
        if (!window.confirm(`¿Estás seguro de ajustar ${tripsToAdjust.length} viajes en EmiGo con la tarifa reportada por Booking?`)) {
            return;
        }
        setIsApplyingAdjustments(true);
        try {
            const adjustmentsPayload = tripsToAdjust.map(t => ({
                trip_id: t.trip_id,
                real_paid_amount: t.booking_paid
            }));
            
            await apiClient.post('/transport/booking/apply-adjustments', adjustmentsPayload);
            alert("Ajustes aplicados correctamente en EmiGo.");
            
            if (bookingFile) {
                handleUploadBookingReport();
            }
        } catch (err) {
            alert("Error al aplicar ajustes: " + (err.response?.data?.detail || err.message));
        } finally {
            setIsApplyingAdjustments(false);
        }
    };

    const handlePrefacturarConciliados = async () => {
        if (!reconciliationData || reconciliationData.matched.length === 0) return;
        
        // 1. Filtrar los que ya están prefacturados/facturados en EmiGo (client_invoice_id no nulo)
        const alreadyPrefactured = reconciliationData.matched.filter(t => t.client_invoice_id !== null && t.client_invoice_id !== undefined);
        const pendingToPrefacture = reconciliationData.matched.filter(t => t.client_invoice_id === null || t.client_invoice_id === undefined);
        const pendingIds = pendingToPrefacture.map(t => t.trip_id);
        
        if (pendingIds.length === 0) {
            alert(`Todos los ${reconciliationData.matched.length} viajes conciliados en este reporte ya se encuentran pre-facturados en el sistema.`);
            return;
        }
        
        let confirmMsg = `¿Deseas crear la Pre-Factura en EmiGo para los ${pendingIds.length} viajes conciliados y correctos?`;
        if (alreadyPrefactured.length > 0) {
            confirmMsg = `De los ${reconciliationData.matched.length} viajes conciliados, ${alreadyPrefactured.length} ya se encuentran pre-facturados anteriormente.\n\n¿Deseas pre-facturar los ${pendingIds.length} viajes restantes que aún no han sido procesados?`;
        }
        
        if (!window.confirm(confirmMsg)) {
            return;
        }
        
        const bookingClient = clients.find(c => c.name.toUpperCase() === "BOOKING");
        const bookingClientId = bookingClient ? bookingClient.id : 1;

        setLoading(true);
        try {
            await apiClient.post('transport/invoices/pre-invoice', {
                client_id: bookingClientId,
                trip_ids: pendingIds,
                billing_period_label: `Cobro Booking ${format(new Date(), 'MMM yyyy')}`,
                grouping_type: 'general'
            });
            alert(`Pre-factura creada con éxito para ${pendingIds.length} viajes.`);
            setReconciliationData(null);
            setBookingFile(null);
            await loadPendingData();
            loadInvoices();
        } catch (err) {
            alert("Error al generar la pre-factura: " + (err.response?.data?.detail || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleBulkGenerate = async () => {
        if (selectedBulkClients.length === 0) return;
        setLoading(true);
        try {
            const payload = [];
            for (const cId of selectedBulkClients) {
                const previewItem = bulkPreview.find(p => p.client_id === cId);
                const tripIds = previewItem?.trips?.map(t => t.id) || [];
                if (tripIds.length > 0) {
                    payload.push({
                        client_id: cId,
                        trip_ids: tripIds,
                        billing_period_label: `Facturación Masiva ${format(new Date(), 'dd/MM/yy')}`,
                        grouping_type: 'general'
                    });
                }
            }
            if (payload.length > 0) await apiClient.post('transport/invoices/bulk-generate', payload);
            setShowBulkModal(false);
            await loadPendingData();
            loadInvoices(true);
            alert(`Lote procesado: ${payload.length} facturas generadas.`);
        } catch (e) { alert("Error en procesamiento masivo"); } finally { setLoading(false); }
    };

    const calculateBalances = (inv) => {
        const docs = inv?.documents || [];
        const invoiced = docs.reduce((acc, doc) => {
            if (doc.type === 'FACTURA') return acc + (doc.amount || 0);
            if (doc.type === 'NC') return acc - (doc.amount || 0);
            return acc;
        }, 0);
        return {
            total: inv?.total_amount || 0,
            invoiced: invoiced,
            pending: (inv?.total_amount || 0) - invoiced
        };
    };

    const handleOpenDocManager = async (inv) => {
        setLoadingInvoicesList(true);
        try {
            const res = await apiClient.get(`transport/invoices/${inv.id}`);
            const fullInv = res.data;
            setActiveInvoice(fullInv);
            const balances = calculateBalances(fullInv);
            setNewDocAmount(balances.pending > 0 ? Math.round(balances.pending) : 0);
            setNewDocFolio('');
            setNewDocOC('');
            setNewDocHES('');
            setShowDocManagerModal(true);
        } catch (e) {
            console.error(e);
            alert("Error al cargar los detalles de la pre-factura");
        } finally {
            setLoadingInvoicesList(false);
        }
    };

    const handleMarkPaid = async () => {
        setLoading(true);
        try {
            if (activeDocument) {
                await apiClient.put(`transport/invoices/documents/${activeDocument.id}/paid`, {
                    payment_method: payMethod, 
                    webpay_commission: parseFloat(webpayCommission) || 0,
                    pending_commission: pendingCommission
                });
                // REFRESCO INTELIGENTE: Traemos solo la factura modificada por su ID
                const res = await apiClient.get(`transport/invoices/${activeInvoice.id}`);
                const updatedInvoice = res.data;
                setActiveInvoice(updatedInvoice);
                setInvoices(prev => prev.map(i => i.id === updatedInvoice.id ? updatedInvoice : i));
            } else if (activeTrip) {
                await apiClient.post(`transport/trips/${activeTrip.id}/particular/confirm`, {
                    payment_method: payMethod, webpay_commission: parseFloat(webpayCommission) || 0
                });
                loadParticularTrips();
            }
            setShowPayModal(false); 
            setWebpayCommission(0);
            setPendingCommission(false);
            setActiveTrip(null); 
            setActiveDocument(null);
            // No hacemos setActiveInvoice(null) aquí para que el modal de documentos siga vivo
        } catch (e) { alert("Error al procesar el pago"); } finally { setLoading(false); }
    };

    const handleDeleteInvoice = async () => {
        setLoading(true);
        try {
            await apiClient.delete(`transport/invoices/${activeInvoice.id}`);
            setShowDeleteModal(false);
            loadInvoices(true);
            await loadPendingData();
        } catch (e) { alert("Error"); } finally { setLoading(false); }
    };

    const handleInitParticular = async (tripId) => {
        if (!window.confirm("¿Registrar cobro de este particular en el Flujo?")) return;
        try {
            await apiClient.post(`transport/trips/${tripId}/particular/init`);
            loadParticularTrips();
        } catch (e) { alert("Error"); }
    };

    const handleRevertParticularPayment = async (tripId) => {
        if (!window.confirm("¿Está seguro de que desea revertir el pago de este servicio? El estado del servicio volverá a estar Pendiente de Pago y la transacción asociada se actualizará en el Flujo de Caja.")) return;
        setLoading(true);
        try {
            await apiClient.post(`transport/trips/${tripId}/particular/revert`);
            loadParticularTrips();
            alert("Pago revertido exitosamente");
        } catch (e) {
            alert("Error al revertir el pago");
        } finally {
            setLoading(false);
        }
    };

    const downloadFile = (id, grouping, format) => {
        const baseUrl = apiClient.defaults.baseURL;
        const endpoint = format === 'excel' ? 'export/excel' : 'export/pdf';
        window.open(`${baseUrl}/transport/invoices/${id}/${endpoint}?grouping=${grouping}`, '_blank');
    };

    const handleOpenLogs = async (inv) => {
        try {
            setSelectedInvoiceForLogs(inv);
            setLoadingLogs(true);
            setShowLogsModal(true);
            const res = await apiClient.get(`transport/invoices/${inv.id}/logs`);
            setInvoiceLogs(res.data);
        } catch (e) {
            console.error(e);
            alert("Error al cargar los logs de auditoría");
        } finally {
            setLoadingLogs(false);
        }
    };

    const filteredInvoices = invoices.filter(inv => {
        if (trackFolio.trim()) {
            const cleanFolio = trackFolio.trim().toLowerCase();
            const matchDoc = inv.documents?.some(doc => 
                String(doc.folio || '').toLowerCase().includes(cleanFolio) ||
                String(doc.oc_number || '').toLowerCase().includes(cleanFolio) ||
                String(doc.hes_number || '').toLowerCase().includes(cleanFolio)
            );
            const matchInternal = String(inv.internal_number || '').toLowerCase().includes(cleanFolio);
            if (!matchDoc && !matchInternal) return false;
        }
        if (trackPreInvoice.trim()) {
            const cleanPre = trackPreInvoice.trim().toLowerCase();
            if (!String(inv.internal_number || '').toLowerCase().includes(cleanPre)) return false;
        }
        
        if (trackClient && (inv.client?.id || inv.client_id) !== parseInt(trackClient)) return false;
        
        if (trackStartDate && inv.created_at) {
            const d = inv.created_at.split('T')[0];
            if (d < trackStartDate) return false;
        }
        if (trackEndDate && inv.created_at) {
            const d = inv.created_at.split('T')[0];
            if (d > trackEndDate) return false;
        }
        return true;
    });

    return (
        <div className="p-3">
            <h3 className="mb-4 text-primary fw-bold">Centro de Facturación y Cobranza</h3>

            <Tabs activeKey={activeTab} onSelect={k => setActiveTab(k)} className="mb-4 custom-tabs">
                <Tab eventKey="corporativo" title={<span><CreditCard size={16} className="me-2"/> Clientes Empresa</span>}>
                    <Row className="g-4">
                        <Col lg={12}>
                            <Card className="shadow-sm border-0 mb-4 overflow-hidden">
                                <Card.Header className="text-white d-flex justify-content-between align-items-center py-3" style={{ backgroundColor: '#0f172a' }}>
                                    <div className="fw-bold"><FileText size={18} className="me-2" /> 1. Seleccionar Servicios para Cobro</div>
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="d-flex gap-2 me-3">
                                            <div className="bg-white bg-opacity-10 rounded px-2 py-1 text-end">
                                                <div style={{fontSize:'9px', fontWeight:'bold', opacity:0.8, lineHeight:1}}>SERVICIOS</div>
                                                <div className="fw-bold" style={{fontSize:'20px', lineHeight:1}}>
                                                    {isBulkMode ? bulkPreview.reduce((sum, p) => sum + p.trip_count, 0) : filteredTrips.length}
                                                </div>
                                            </div>
                                            <div className="bg-white bg-opacity-20 rounded px-3 py-1 text-end border-start border-white border-opacity-25">
                                                <div style={{fontSize:'9px', fontWeight:'bold', opacity:0.8, lineHeight:1}}>TOTAL VENTA</div>
                                                <div className="fw-bold text-warning" style={{fontSize:'20px', lineHeight:1}}>
                                                    ${(isBulkMode ? bulkPreview.reduce((sum, p) => sum + p.total_amount, 0) : filteredTrips.reduce((sum, t) => sum + (parseFloat(t.estimated_price) || 0) + (parseFloat(t.waiting_time_amount) || 0) + (t.extra_services || []).reduce((acc, s) => acc + (parseFloat(s.price) || 0), 0), 0)).toLocaleString()}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-3">
                                            <Form.Check 
                                                type="switch" 
                                                id="bulk-mode-switch"
                                                label="Vista Agrupada" 
                                                checked={isBulkMode} 
                                                onChange={e => setIsBulkMode(e.target.checked)} 
                                                className="fw-bold" 
                                            />
                                            {isBulkMode && (
                                                <ButtonGroup size="sm" className="shadow-sm">
                                                    <Button 
                                                        variant={bulkFilterType === 'masivos' ? 'warning' : 'outline-light'} 
                                                        className="fw-bold px-3" 
                                                        onClick={() => setBulkFilterType('masivos')}
                                                    >
                                                        📦 Clientes Masivos
                                                    </Button>
                                                    <Button 
                                                        variant={bulkFilterType === 'no_masivos' ? 'info' : 'outline-light'} 
                                                        className="fw-bold px-3 text-white" 
                                                        onClick={() => setBulkFilterType('no_masivos')}
                                                    >
                                                        🏢 Clientes No Masivos
                                                    </Button>
                                                </ButtonGroup>
                                            )}
                                        </div>
                                    </div>
                                </Card.Header>
                                <Card.Body className="bg-light border-bottom p-4">
                                    <Row className="g-3 mb-3">
                                        {!isBulkMode ? (
                                            <>
                                                <Col md={3}>
                                                    <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Cliente</Form.Label>
                                                    <SearchableSelect 
                                                        options={clients} 
                                                        value={selectedClient} 
                                                        onChange={val => setSelectedClient(val)} 
                                                        placeholder="- Seleccionar Cliente -"
                                                    />
                                                </Col>
                                                <Col md={2}>
                                                    <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Área</Form.Label>
                                                    <SearchableSelect 
                                                        options={clientAreas} 
                                                        value={selectedArea} 
                                                        onChange={val => setSelectedArea(val)} 
                                                        disabled={!selectedClient} 
                                                        placeholder="Todas las Áreas"
                                                    />
                                                </Col>
                                                <Col md={2}>
                                                    <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>CC</Form.Label>
                                                    <SearchableSelect 
                                                        options={clientCCs.map(cc => ({ id: cc.id, name: `${cc.code} - ${cc.name}` }))} 
                                                        value={selectedCC} 
                                                        onChange={val => setSelectedCC(val)} 
                                                        disabled={!selectedClient} 
                                                        placeholder="Todos los CC"
                                                    />
                                                </Col>
                                            </>
                                        ) : (
                                            <Col md={7} className="d-flex align-items-center">
                                                <div className="bg-white p-2 rounded border border-primary w-100">
                                                    <Layers size={16} className="text-primary me-2"/>
                                                    <span className="small text-primary fw-bold text-uppercase">Lote de Facturación Masiva Activo</span>
                                                    <small className="text-muted ms-2">(Se filtran solo clientes con check "Facturación Masiva")</small>
                                                </div>
                                            </Col>
                                        )}
                                        <Col md={2}>
                                            <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Desde</Form.Label>
                                            <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} size="sm" />
                                        </Col>
                                        <Col md={2}>
                                            <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Hasta</Form.Label>
                                            <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} size="sm" />
                                        </Col>
                                        <Col md={1} className="d-flex align-items-end">
                                            <Button variant="primary" size="sm" className="w-100 shadow-sm" onClick={loadPendingData}><Filter size={14}/></Button>
                                        </Col>
                                    </Row>
                                     {!isBulkMode && (() => {
                                         const activeClientObj = clients.find(c => c.id === parseInt(selectedClient));
                                         return activeClientObj ? (
                                             <div className="border-0 shadow-sm p-3 mb-3 d-flex align-items-center justify-content-between rounded-3 animate__animated animate__fadeIn" style={{ backgroundColor: '#f8fafc', borderLeft: '4px solid #4f46e5' }}>
                                                 <div className="d-flex align-items-center gap-3">
                                                     <div className="p-2 rounded-circle" style={{ backgroundColor: 'rgba(79, 70, 229, 0.1)', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                         <FileText size={20} />
                                                     </div>
                                                     <div>
                                                         <div className="fw-bold text-dark text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>Regla / Cortapalo de Facturación</div>
                                                         <div className="text-secondary small mt-1" style={{ fontSize: '12px' }}>
                                                             {activeClientObj.billing_notes ? (
                                                                 <strong className="text-dark">{activeClientObj.billing_notes}</strong>
                                                             ) : (
                                                                 <span className="text-muted italic">Sin instrucciones de cortapalo registradas para este convenio.</span>
                                                             )}
                                                         </div>
                                                     </div>
                                                 </div>
                                                 <div className="text-end">
                                                     <span className="small text-muted d-block fw-bold" style={{ fontSize: '9px', letterSpacing: '0.5px' }}>FORMA DE COBRO</span>
                                                     <Badge bg="dark" className="fw-bold px-2 py-1 mt-1 text-uppercase" style={{ fontSize: '11px', backgroundColor: '#312e81' }}>
                                                         {activeClientObj.billing_process_type || 'Se puede facturar de inmediato'}
                                                     </Badge>
                                                 </div>
                                             </div>
                                         ) : null;
                                     })()}
                                    {!isBulkMode && (
                                        <Row className="g-2">
                                            <Col md={2}>
                                                <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Naturaleza</Form.Label>
                                                <Form.Control placeholder="Ej: Traslado Personal" value={expenseNature} onChange={e => setExpenseNature(e.target.value)} size="sm" />
                                            </Col>
                                            <Col md={2}>
                                                <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>OS / Referencia</Form.Label>
                                                <InputGroup size="sm">
                                                    <InputGroup.Text className="bg-white"><Search size={14} className="text-muted"/></InputGroup.Text>
                                                    <Form.Control placeholder="Buscar Ref..." value={externalRef} onChange={e => setExternalRef(e.target.value)} />
                                                </InputGroup>
                                            </Col>
                                            <Col md={2}>
                                                <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Pasajero</Form.Label>
                                                <InputGroup size="sm">
                                                    <InputGroup.Text className="bg-white"><User size={14} className="text-muted"/></InputGroup.Text>
                                                    <Form.Control placeholder="Nombre..." value={passengerName} onChange={e => setPassengerName(e.target.value)} />
                                                </InputGroup>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Archivo (File)</Form.Label>
                                                <SearchableSelect 
                                                    options={fileOptions} 
                                                    value={selectedFile} 
                                                    onChange={val => setSelectedFile(val)} 
                                                    placeholder="Todos los Archivos (File)"
                                                />
                                            </Col>
                                            <Col md={3} className="d-flex align-items-end gap-1">
                                                <Button 
                                                    variant="outline-info" 
                                                    size="sm" 
                                                    className="fw-bold text-nowrap"
                                                    title="Ver todas las Órdenes de Compra registradas y asociarles Factura SII"
                                                    onClick={() => {
                                                        loadOCSummary();
                                                        setShowOCManagerModal(true);
                                                    }}
                                                >
                                                    📋 Ver OCs
                                                </Button>
                                                <Button 
                                                    variant={isFutureMode ? "primary" : "outline-secondary"} 
                                                    size="sm" 
                                                    className="fw-bold text-nowrap"
                                                    title={isFutureMode ? "Volver a la vista de servicios procesados listos para pre-facturar" : "Ver servicios programados/futuros para asignarles OC anticipada"}
                                                    onClick={() => {
                                                        setIsFutureMode(!isFutureMode);
                                                        setSelectedTripsIds([]);
                                                    }}
                                                >
                                                    📅 {isFutureMode ? "Ver Procesados" : "Servicios Futuros"}
                                                </Button>
                                                {isFutureMode ? (
                                                    <Button 
                                                        variant="success" 
                                                        className="w-100 fw-bold shadow-sm text-nowrap" 
                                                        disabled={selectedTripIds.length === 0 || loading} 
                                                        onClick={() => setShowAssignOCModal(true)} 
                                                        size="sm"
                                                    >
                                                        <FileText size={15} className="me-1" /> Ingresar OC ({selectedTripIds.length})
                                                    </Button>
                                                ) : (
                                                    <Button 
                                                        variant="success" 
                                                        className="w-100 fw-bold shadow-sm text-nowrap" 
                                                        disabled={selectedTripIds.length === 0 || loading} 
                                                        onClick={() => setShowPreInvoiceModal(true)} 
                                                        size="sm"
                                                    >
                                                        <CheckCircle size={15} className="me-1" /> Pre-Facturar ({selectedTripIds.length})
                                                    </Button>
                                                )}
                                            </Col>
                                        </Row>
                                    )}
                                    {isBulkMode && (
                                        <Row className="justify-content-end">
                                            <Col md="auto" className="d-flex align-items-end">
                                                <Button variant="outline-primary" size="sm" className="me-2 fw-bold" onClick={() => setExpandedBulkClients(expandedBulkClients.length === bulkPreview.length ? [] : bulkPreview.map(p => p.client_id))}>
                                                    {expandedBulkClients.length === bulkPreview.length ? 'Contraer Todo' : 'Expandir Todo'}
                                                </Button>
                                                <Button variant="success" className="fw-bold shadow-sm px-4" disabled={selectedBulkClients.length === 0 || loading} onClick={() => setShowBulkModal(true)} size="sm">
                                                    <Layers size={16} className="me-2" /> Facturar Lote ({selectedBulkClients.length})
                                                </Button>
                                            </Col>
                                        </Row>
                                    )}
                                </Card.Body>
                                {!isBulkMode ? (
                                    <Table hover responsive size="sm" className="mb-0 align-middle" style={{fontSize: '12px'}}>
                                        <thead className="bg-white sticky-top shadow-sm">
                                            <tr>
                                                <th className="px-3 py-2"><Form.Check type="checkbox" onChange={selectAll} /></th>
                                                <th>ID</th>
                                                <th>Fecha/Hora</th>
                                                <th>Pasajero</th>
                                                <th>Ref</th>
                                                <th>Archivo (File)</th>
                                                <th>OC</th>
                                                <th className="text-end px-3">Venta</th>
                                                <th>Área / CC</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? <tr><td colSpan="9" className="text-center py-5"><Spinner animation="border" variant="primary" size="sm" className="me-2"/> Cargando servicios...</td></tr> : 
                                            filteredTrips.length === 0 ? <tr><td colSpan="9" className="text-center py-5 text-muted">{isFutureMode ? "No se encontraron servicios futuros pendientes." : "No se encontraron servicios pendientes de pre-facturación."}</td></tr> :
                                            filteredTrips.map(t => (
                                                <tr 
                                                    key={t.id} 
                                                    className={selectedTripIds.includes(t.id) ? 'table-primary' : ''}
                                                    style={!selectedTripIds.includes(t.id) && t.oc_number ? { backgroundColor: '#fff8e1', borderLeft: '4px solid #ffb300' } : undefined}
                                                >
                                                    <td className="px-3"><Form.Check type="checkbox" checked={selectedTripIds.includes(t.id)} onChange={() => toggleTrip(t.id)} /></td>
                                                    <td className="fw-bold">
                                                        #{t.id}
                                                        {t.oc_number && (
                                                            <div>
                                                                <Badge bg="warning" text="dark" className="fw-bold border border-warning shadow-sm mt-1" style={{ fontSize: '10px' }}>
                                                                    📋 OC #{t.oc_number}
                                                                </Badge>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>{format(new Date(t.scheduled_time), 'dd/MM HH:mm')}</td>
                                                    <td>{t.passenger_name}</td>
                                                    <td><Badge bg="light" text="dark" className="border">{t.external_reference || '-'}</Badge></td>
                                                    <td><span className="text-muted small">{t.file || '-'}</span></td>
                                                    <td>
                                                        {t.oc_number ? (
                                                            <Badge bg="warning" text="dark" className="fw-bold border border-warning shadow-sm" style={{ fontSize: '11px', padding: '5px 8px' }} title={`Esta reserva ya tiene la OC #${t.oc_number} asignada`}>
                                                                📋 OC #{t.oc_number}
                                                            </Badge>
                                                        ) : (
                                                            <span className="text-muted small">-</span>
                                                        )}
                                                    </td>
                                                    <td className="text-end fw-bold px-3 text-primary">
                                                        {(() => {
                                                            const waitAmount = parseFloat(t.waiting_time_amount) || 0;
                                                            const extras = (t.extra_services || []).reduce((acc, s) => acc + (parseFloat(s.price) || 0), 0);
                                                            const totalVenta = (parseFloat(t.estimated_price) || 0) + waitAmount + extras;
                                                            
                                                            if (waitAmount > 0 || extras > 0) {
                                                                return (
                                                                    <OverlayTrigger
                                                                        placement="top"
                                                                        overlay={
                                                                            <Tooltip id={`tooltip-venta-${t.id}`}>
                                                                                <div className="text-start">
                                                                                    <strong>Venta Base:</strong> ${Math.round(t.estimated_price || 0).toLocaleString()}<br/>
                                                                                    {extras > 0 && (
                                                                                        <>
                                                                                            <strong>Adicionales:</strong> ${Math.round(extras).toLocaleString()}<br/>
                                                                                        </>
                                                                                    )}
                                                                                    {waitAmount > 0 && (
                                                                                        <>
                                                                                            <strong>Espera:</strong> ${Math.round(waitAmount).toLocaleString()}<br/>
                                                                                        </>
                                                                                    )}
                                                                                </div>
                                                                            </Tooltip>
                                                                        }
                                                                    >
                                                                        <div className="cursor-help">${Math.round(totalVenta).toLocaleString()} <small>➕</small></div>
                                                                    </OverlayTrigger>
                                                                );
                                                            }
                                                            return `$${Math.round(totalVenta).toLocaleString()}`;
                                                        })()}
                                                    </td>
                                                    <td><Badge bg="info" className="fw-normal">{t.area?.name || t.area_label || '-'}</Badge> / <Badge bg="secondary" className="fw-normal">{t.cost_center?.code || t.cost_center_label || '-'}</Badge></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <Table hover responsive size="sm" className="mb-0 align-middle" style={{fontSize: '13px'}}>
                                        <thead className="bg-white sticky-top shadow-sm">
                                            <tr>
                                                <th style={{width: '40px'}}></th>
                                                <th className="px-3 py-2" style={{width: '40px'}}>
                                                    <Form.Check type="checkbox" checked={selectedBulkClients.length === bulkPreview.length} onChange={e => setSelectedBulkClients(e.target.checked ? bulkPreview.map(p => p.client_id) : [])} />
                                                </th>
                                                <th>Empresa Corporativa</th>
                                                <th className="text-center">Cant. Viajes</th>
                                                <th className="text-end px-3">Monto Neto Lote</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? <tr><td colSpan="5" className="text-center py-5"><Spinner animation="border" variant="success" size="sm" className="me-2"/> Calculando lotes...</td></tr> : 
                                            bulkPreview.length === 0 ? <tr><td colSpan="5" className="text-center py-5 text-muted">No hay clientes con facturación masiva y viajes pendientes.</td></tr> :
                                            bulkPreview.map(p => (
                                                <React.Fragment key={p.client_id}>
                                                    <tr className={selectedBulkClients.includes(p.client_id) ? 'table-success' : ''}>
                                                        <td className="text-center">
                                                            <Button variant="link" size="sm" className="p-0 text-dark" onClick={() => toggleBulkRow(p.client_id)}>
                                                                {expandedBulkClients.includes(p.client_id) ? <ChevronDown size={18}/> : <ChevronRight size={18}/>}
                                                            </Button>
                                                        </td>
                                                        <td className="px-3">
                                                            <Form.Check type="checkbox" checked={selectedBulkClients.includes(p.client_id)} onChange={() => setSelectedBulkClients(prev => prev.includes(p.client_id) ? prev.filter(id => id !== p.client_id) : [...prev, p.client_id])} />
                                                        </td>
                                                        <td className="fw-bold">{p.client_name}</td>
                                                        <td className="text-center"><Badge bg="primary" pill>{p.trip_count} servicios</Badge></td>
                                                        <td className="text-end fw-bold px-3 text-success">${p.total_amount?.toLocaleString()}</td>
                                                    </tr>
                                                    {expandedBulkClients.includes(p.client_id) && (
                                                        <tr>
                                                            <td colSpan="5" className="bg-white p-0">
                                                                <div className="p-3 border-start border-4 border-success ms-4 my-2 shadow-sm rounded bg-light bg-opacity-50">
                                                                    <h6 className="small fw-bold text-success mb-2 uppercase">Detalle de Reservas Pendientes</h6>
                                                                    <Table size="sm" hover className="mb-0 bg-white shadow-sm" style={{fontSize: '11px'}}>
                                                                        <thead className="bg-light text-muted">
                                                                            <tr>
                                                                                <th>ID</th>
                                                                                <th>Fecha/Hora</th>
                                                                                <th>Pasajero</th>
                                                                                <th>Ruta (Origen / Destino)</th>
                                                                                <th>Centro Costo / Área</th>
                                                                                <th>OS / Ref</th>
                                                                                <th className="text-center">Móvil</th>
                                                                                <th className="text-end">Monto</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {p.trips?.map(trip => (
                                                                                <tr key={trip.id}>
                                                                                    <td className="fw-bold text-muted">#{trip.id}</td>
                                                                                    <td>{format(new Date(trip.scheduled_time), 'dd/MM/yy HH:mm')}</td>
                                                                                    <td className="fw-bold">{trip.passenger_name}</td>
                                                                                    <td style={{maxWidth: '200px'}}>
                                                                                        <div className="text-truncate" title={trip.pickup_address}><span className="text-success small">🟢</span> {trip.pickup_address}</div>
                                                                                        <div className="text-truncate mt-1" title={trip.dropoff_address}><span className="text-danger small">🏁</span> {trip.dropoff_address}</div>
                                                                                    </td>
                                                                                    <td>
                                                                                        <Badge bg="secondary" className="fw-normal me-1">{trip.cc_code}</Badge>
                                                                                        <Badge bg="info" className="fw-normal">{trip.area_name}</Badge>
                                                                                    </td>
                                                                                    <td><Badge bg="light" text="dark" className="border">{trip.external_reference || '-'}</Badge></td>
                                                                                    <td className="text-center fw-bold">{trip.vehicle_code}</td>
                                                                                    <td className="text-end fw-bold text-primary">
                                                                                        ${(trip.estimated_price || 0).toLocaleString()}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </Table>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Card>
                        </Col>
                        
                        <Col lg={12}>
                            <Card className="shadow-sm border-0">
                                <Card.Header className="bg-dark text-white fw-bold py-3"><CreditCard size={18} className="me-2" /> 2. Seguimiento Facturas Empresa</Card.Header>
                                <Card.Body className="bg-light border-bottom p-3">
                                    <Row className="g-2 align-items-end">
                                        <Col md={2}>
                                            <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Factura (Folio)</Form.Label>
                                            <Form.Control 
                                                size="sm" 
                                                placeholder="Buscar Folio..." 
                                                value={trackFolio} 
                                                onChange={e => setTrackFolio(e.target.value)} 
                                                onKeyDown={e => { if (e.key === 'Enter') loadInvoices(true); }}
                                            />
                                        </Col>
                                        <Col md={2}>
                                            <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Pre-Factura (PF-...)</Form.Label>
                                            <Form.Control 
                                                size="sm" 
                                                placeholder="Buscar PF..." 
                                                value={trackPreInvoice} 
                                                onChange={e => setTrackPreInvoice(e.target.value)} 
                                                onKeyDown={e => { if (e.key === 'Enter') loadInvoices(true); }}
                                            />
                                        </Col>
                                        <Col md={2}>
                                            <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Cliente</Form.Label>
                                            <Form.Select size="sm" value={trackClient} onChange={e => setTrackClient(e.target.value)} disabled={trackFolio || trackPreInvoice}>
                                                <option value="">Todos</option>
                                                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                            </Form.Select>
                                        </Col>
                                        <Col md={2}>
                                            <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Área / CC</Form.Label>
                                            <Form.Control 
                                                size="sm" 
                                                placeholder="Buscar Área o CC..." 
                                                value={trackArea} 
                                                onChange={e => setTrackArea(e.target.value)} 
                                                disabled={trackFolio || trackPreInvoice} 
                                                onKeyDown={e => { if (e.key === 'Enter') loadInvoices(true); }}
                                            />
                                        </Col>
                                        <Col md={2}>
                                            <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Desde (Creación)</Form.Label>
                                            <Form.Control type="date" size="sm" value={trackStartDate} onChange={e => setTrackStartDate(e.target.value)} disabled={trackFolio || trackPreInvoice} />
                                        </Col>
                                        <Col md={2}>
                                            <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Hasta (Creación)</Form.Label>
                                            <Form.Control type="date" size="sm" value={trackEndDate} onChange={e => setTrackEndDate(e.target.value)} disabled={trackFolio || trackPreInvoice} />
                                        </Col>
                                    </Row>
                                    <Row className="g-2 align-items-end mt-2 justify-content-end">
                                        <Col xs="auto">
                                            <Button variant="secondary" size="sm" className="fw-bold" onClick={handleClearFilters}>
                                                <RotateCcw size={14} className="me-1"/> Limpiar
                                            </Button>
                                        </Col>
                                        <Col xs="auto">
                                            <Button variant="primary" size="sm" className="fw-bold px-4" onClick={() => loadInvoices(true)} disabled={loadingInvoicesList}>
                                                <Search size={14} className="me-1"/> Buscar
                                            </Button>
                                        </Col>
                                    </Row>
                                </Card.Body>
                                <Card.Body className="p-0">
                                    <Table hover responsive size="sm" className="align-middle mb-0" style={{fontSize:'13px'}}>
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="px-3">Pre-Factura</th>
                                                <th>Cliente</th>
                                                <th>Facturas SII</th>
                                                <th className="text-end">Monto Total</th>
                                                <th className="text-end">Saldo Pendiente</th>
                                                <th className="text-center">Pref. Enviada</th>
                                                <th className="text-center">Fact. Enviada</th>
                                                <th className="text-center">Estado</th>
                                                <th className="text-end px-3">Acciones / Documentos</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loadingInvoicesList ? (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-5 text-muted">
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Cargando pre-facturas...
                                                    </td>
                                                </tr>
                                            ) : filteredInvoices.length === 0 ? (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-5 text-muted">
                                                        {(!trackFolio && !trackPreInvoice && !trackClient && !trackArea && !trackStartDate && !trackEndDate) ? (
                                                            <div className="py-3">
                                                                <Search size={32} className="text-muted mb-2 opacity-50"/>
                                                                <p className="mb-0 fw-bold">Búsqueda de Pre-facturas</p>
                                                                <small className="text-muted">Ingrese algún filtro arriba (como número de prefactura o cliente) y presione <strong>Buscar</strong> para visualizar los registros.</small>
                                                            </div>
                                                        ) : (
                                                            "No se encontraron pre-facturas que coincidan con los filtros ingresados."
                                                        )}
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredInvoices.map(inv => {
                                                    const balances = calculateBalances(inv);
                                                    return (
                                                    <tr key={inv.id}>
                                                         <td className="px-3 fw-bold">
                                                             <span
                                                                 className="text-primary text-decoration-underline"
                                                                 style={{ cursor: 'pointer' }}
                                                                 onClick={() => handleOpenLogs(inv)}
                                                                 title="Ver historial de auditoría"
                                                             >
                                                                 {inv.internal_number}
                                                             </span>
                                                         </td>
                                                        <td>{inv.client?.name}</td>
                                                        <td>
                                                            {inv.documents?.filter(d => d.type === 'FACTURA' && d.folio).map(d => (
                                                                <Badge
                                                                    key={d.id}
                                                                    bg={d.status === 'cancelled' ? 'secondary' : 'primary'}
                                                                    className="me-1"
                                                                    style={{
                                                                        fontSize: '10px',
                                                                        textDecoration: d.status === 'cancelled' ? 'line-through' : 'none',
                                                                        opacity: d.status === 'cancelled' ? 0.65 : 1
                                                                    }}
                                                                    title={d.status === 'cancelled' ? 'Factura anulada por Nota de Crédito' : undefined}
                                                                >
                                                                    #{d.folio}
                                                                </Badge>
                                                            ))}
                                                            {(!inv.documents || inv.documents.filter(d => d.type === 'FACTURA' && d.folio).length === 0) && (
                                                                <span className="text-muted opacity-50">-</span>
                                                            )}
                                                        </td>
                                                        <td className="text-end">
                                                             {((inv.trip_count ?? (inv.trips?.length || 0)) === 0) ? (
                                                                 <Badge bg="danger" style={{fontSize: '10px'}}>SIN RESERVAS</Badge>
                                                             ) : (
                                                                 <span className="fw-bold">${inv.total_amount?.toLocaleString()}</span>
                                                             )}
                                                         </td>
                                                        <td className={`fw-bold text-end ${balances.pending > 0 ? 'text-danger' : 'text-success'}`}>
                                                            ${balances.pending.toLocaleString()}
                                                        </td>
                                                        <td className="text-center">
                                                            <Form.Check 
                                                                type="checkbox" 
                                                                checked={inv.is_sent || false} 
                                                                disabled={inv.status === 'cancelled'}
                                                                onChange={async () => {
                                                                    try {
                                                                        await apiClient.put(`transport/invoices/${inv.id}/sent-status`);
                                                                        loadInvoices(true);
                                                                    } catch (e) { alert("Error al actualizar estado de envío de la pre-factura"); }
                                                                }} 
                                                            />
                                                        </td>
                                                        <td className="text-center">
                                                            {(() => {
                                                                const firstFactura = inv.documents?.find(d => d.type === 'FACTURA');
                                                                if (!firstFactura) {
                                                                    return <span className="text-muted small italic" style={{fontSize: '10px'}}>Sin Factura</span>;
                                                                }
                                                                return (
                                                                    <Form.Check 
                                                                        type="checkbox" 
                                                                        checked={firstFactura.is_sent || false} 
                                                                        disabled={inv.status === 'cancelled'}
                                                                        onChange={async () => {
                                                                            try {
                                                                                await apiClient.put(`transport/invoices/documents/${firstFactura.id}/sent-status`);
                                                                                loadInvoices(true);
                                                                            } catch (e) { alert("Error al actualizar estado de envío de la factura"); }
                                                                        }} 
                                                                    />
                                                                );
                                                            })()}
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg={{
                                                                'pre_invoiced': 'secondary', 
                                                                'partially_invoiced': 'warning',
                                                                'invoiced': 'primary', 
                                                                'paid': 'success',
                                                                'cancelled': 'danger'
                                                            }[inv.status] || 'dark'}>
                                                                {{
                                                                    'pre_invoiced': 'PRE-FACTURADA',
                                                                    'partially_invoiced': 'FACTURADA PARCIAL',
                                                                    'invoiced': 'FACTURADA',
                                                                    'paid': 'PAGADA',
                                                                    'cancelled': 'ANULADA'
                                                                }[inv.status] || inv.status.toUpperCase()}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-end px-3">
                                                            <ButtonGroup size="sm">
                                                                <Dropdown as={ButtonGroup}>
                                                                    <Button variant="light" size="sm" title="Descargar Detalle" disabled={inv.status === 'cancelled'}><Download size={14}/></Button>
                                                                    <Dropdown.Toggle split variant="light" disabled={inv.status === 'cancelled'} />
                                                                    <Dropdown.Menu className="shadow border-0" style={{fontSize:'12px'}}>
                                                                        {[
                                                                            { label: 'General', group: 'general' },
                                                                            { label: 'Por Centro de Costo', group: 'cost_center' },
                                                                            { label: 'Por Área', group: 'area' },
                                                                            { label: 'Por Pasajero', group: 'passenger' }
                                                                        ].map(opt => (
                                                                            <div key={opt.group} className="d-flex align-items-center px-3 py-1 dropdown-item-custom border-bottom">
                                                                                <span className="flex-grow-1">{opt.label}</span>
                                                                                <Button variant="link" className="p-0 ms-2 text-success" onClick={() => downloadFile(inv.id, opt.group, 'excel')}><FileSpreadsheet size={16}/></Button>
                                                                                <Button variant="link" className="p-0 ms-2 text-danger" onClick={() => downloadFile(inv.id, opt.group, 'pdf')}><FilePdf size={16}/></Button>
                                                                            </div>
                                                                        ))}
                                                                    </Dropdown.Menu>
                                                                </Dropdown>
                                                                <Button variant="primary" onClick={() => handleOpenDocManager(inv)} disabled={inv.status === 'cancelled'}>
                                                                    <FileText size={14} className="me-1" /> Documentos ({inv.documents?.length || 0})
                                                                </Button>
                                                                <Button variant="outline-danger" onClick={() => { setActiveInvoice(inv); setShowDeleteModal(true); }} disabled={inv.status === 'cancelled'}><Trash2 size={14} /></Button>
                                                            </ButtonGroup>
                                                        </td>
                                                    </tr>
                                                )})
                                            )}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Tab>

                <Tab eventKey="particular" title={<span><User size={16} className="me-2"/> Clientes Particulares</span>}>
                    <Card className="shadow-sm border-0 overflow-hidden">
                        <Card.Header className="bg-info text-white d-flex justify-content-between align-items-center py-3">
                            <div className="fw-bold"><User size={18} className="me-2" /> Gestión de Cobros Particulares</div>
                            <div className="d-flex gap-2">
                                <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} size="sm" style={{width:'150px'}}/>
                                <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} size="sm" style={{width:'150px'}}/>
                                <Button variant="light" size="sm" onClick={loadParticularTrips}><Filter size={14}/></Button>
                            </div>
                        </Card.Header>
                        
                        <Card.Body className="bg-light border-bottom p-3">
                            <Row className="g-2">
                                <Col md={2}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Reserva ID</Form.Label>
                                    <Form.Control size="sm" placeholder="# ID..." value={searchId} onChange={e => setSearchId(e.target.value)} />
                                </Col>
                                <Col md={3}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Pasajero</Form.Label>
                                    <Form.Control size="sm" placeholder="Nombre pasajero..." value={searchPassenger} onChange={e => setSearchPassenger(e.target.value)} />
                                </Col>
                                <Col md={3}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Solicitante</Form.Label>
                                    <Form.Control size="sm" placeholder="Nombre o email..." value={searchRequester} onChange={e => setSearchRequester(e.target.value)} />
                                </Col>
                                <Col md={3}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>Estado Flujo</Form.Label>
                                    <Form.Select size="sm" value={searchFlowStatus} onChange={e => setSearchFlowStatus(e.target.value)}>
                                        <option value="">Todos los estados</option>
                                        <option value="pending_review">SIN REGISTRAR</option>
                                        <option value="processed">PENDIENTE PAGO</option>
                                        <option value="settled">PAGO RECIBIDO</option>
                                    </Form.Select>
                                </Col>
                                <Col md={1} className="d-flex align-items-end">
                                    <Button variant="info" size="sm" className="w-100 text-white" onClick={loadParticularTrips}><Search size={14}/></Button>
                                </Col>
                            </Row>
                        </Card.Body>

                        <Card.Body className="p-0">
                            <Table hover responsive size="sm" className="mb-0 align-middle" style={{fontSize: '13px'}}>
                                <thead className="bg-light">
                                    <tr><th className="px-3">ID</th><th>Fecha</th><th>Pasajero</th><th>Solicitante</th><th>Origen/Destino</th><th className="text-end">Monto</th><th>Estado Flujo</th><th className="text-end px-3">Acciones</th></tr>
                                </thead>
                                <tbody>
                                    {particularLoading ? <tr><td colSpan="8" className="text-center py-5"><Spinner animation="border" variant="info" size="sm"/></td></tr> :
                                    particularTrips.length === 0 ? <tr><td colSpan="8" className="text-center py-5 text-muted">No se encontraron servicios particulares.</td></tr> :
                                    particularTrips.map(t => (
                                        <tr key={t.id}>
                                            <td className="px-3 text-muted">#{t.id}</td><td>{format(new Date(t.scheduled_time), 'dd/MM/yy HH:mm')}</td>
                                            <td className="fw-bold">{t.passenger_name}</td>
                                            <td className="text-muted small">
                                                {t.requester?.full_name || t.requester?.email || t.customer_reference || '-'}
                                            </td>
                                            <td className="small text-muted">{t.pickup_address.substring(0,35)}...</td>
                                            <td className="text-end fw-bold text-info">
                                                {(() => {
                                                    const waitAmount = parseFloat(t.waiting_time_amount) || 0;
                                                    const extras = (t.extra_services || []).reduce((acc, s) => acc + (parseFloat(s.price) || 0), 0);
                                                    const totalVenta = (parseFloat(t.estimated_price) || 0) + waitAmount + extras;
                                                    
                                                    if (waitAmount > 0 || extras > 0) {
                                                        return (
                                                            <OverlayTrigger
                                                                placement="top"
                                                                overlay={
                                                                    <Tooltip id={`tooltip-venta-part-${t.id}`}>
                                                                        <div className="text-start">
                                                                            <strong>Venta Base:</strong> ${Math.round(t.estimated_price || 0).toLocaleString()}<br/>
                                                                            {extras > 0 && (
                                                                                <>
                                                                                    <strong>Adicionales:</strong> ${Math.round(extras).toLocaleString()}<br/>
                                                                                </>
                                                                            )}
                                                                            {waitAmount > 0 && (
                                                                                <>
                                                                                    <strong>Espera:</strong> ${Math.round(waitAmount).toLocaleString()}<br/>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <div className="cursor-help">${Math.round(totalVenta).toLocaleString()} <small>➕</small></div>
                                                            </OverlayTrigger>
                                                        );
                                                    }
                                                    return `$${Math.round(totalVenta).toLocaleString()}`;
                                                })()}
                                            </td>
                                            <td>
                                                {!t.validation_folio ? (
                                                    <Badge bg="secondary">SIN REGISTRAR</Badge>
                                                ) : t.admin_status?.toLowerCase() === 'settled' ? (
                                                    <div>
                                                        <Badge bg="success">PAGADO</Badge>
                                                        <div className="mt-1">
                                                            <Badge bg="light" className="text-dark border fw-semibold" style={{ fontSize: '10px' }}>
                                                                💳 {t.payment_method && t.payment_method !== 'VOUCHER' ? t.payment_method : 'TRANSFER'}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                ) : t.admin_status?.toLowerCase() === 'pending_conciliation' ? (
                                                    <div>
                                                        <Badge bg="info" className="text-white">ACTUALIZAR MONTOS</Badge>
                                                        {t.payment_method && (
                                                            <div className="mt-1">
                                                                <Badge bg="light" className="text-dark border fw-semibold" style={{ fontSize: '10px' }}>
                                                                    💳 {t.payment_method}
                                                                </Badge>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <Badge bg="warning" className="text-dark">PENDIENTE PAGO</Badge>
                                                        {t.payment_method && t.payment_method !== 'VOUCHER' && (
                                                            <div className="mt-1">
                                                                <Badge bg="light" className="text-dark border fw-semibold" style={{ fontSize: '10px' }}>
                                                                    💳 {t.payment_method}
                                                                </Badge>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>

                                            <td className="text-end px-3">
                                                {!t.validation_folio ? (
                                                    <Button variant="info" size="sm" className="text-white shadow-sm fw-bold" onClick={() => handleInitParticular(t.id)}>Registrar Cobro</Button>
                                                 ) : (
                                                    <ButtonGroup size="sm">
                                                        <Button variant="outline-danger" title="Ver Voucher PDF" onClick={() => window.open(`${apiClient.defaults.baseURL}/transport/trips/${t.id}/voucher/pdf`, '_blank')}><FilePdf size={14}/></Button>
                                                        {t.admin_status?.toLowerCase() === 'processed' && (
                                                            <Button variant="success" title="Registrar Pago" onClick={() => { setActiveTrip(t); setPayMethod('TRANSFER'); setShowPayModal(true); }}><DollarSign size={14}/></Button>
                                                        )}
                                                        {t.admin_status?.toLowerCase() === 'pending_conciliation' && (
                                                            <Button variant="warning" onClick={() => { setActiveTrip(t); setPayMethod(t.payment_method || 'WEBPAY'); setShowPayModal(true); }} className="text-white">Actualizar</Button>
                                                        )}
                                                        {t.admin_status?.toLowerCase() === 'settled' && (
                                                            <Button 
                                                                variant="outline-primary" 
                                                                title="Editar Medio de Pago" 
                                                                onClick={() => { 
                                                                    setActiveTrip(t); 
                                                                    setPayMethod(t.payment_method && t.payment_method !== 'VOUCHER' ? t.payment_method : 'TRANSFER'); 
                                                                    setShowPayModal(true); 
                                                                }}
                                                            >
                                                                <Edit size={14}/>
                                                            </Button>
                                                        )}
                                                        {['settled', 'pending_conciliation'].includes(t.admin_status?.toLowerCase()) && (
                                                            <Button variant="outline-warning" title="Reversar Pago" onClick={() => handleRevertParticularPayment(t.id)}><RotateCcw size={14}/></Button>
                                                        )}
                                                    </ButtonGroup>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Tab>

                <Tab eventKey="conciliacion_booking" title={<span><Layers size={16} className="me-2"/> Conciliador Booking</span>}>
                    <Card className="shadow-sm border-0 overflow-hidden">
                        <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center py-3">
                            <div className="fw-bold"><Layers size={18} className="me-2" /> Conciliador de Reportes de Pago Booking.com</div>
                            {reconciliationData && (
                                <Badge bg="warning" className="text-dark fw-bold">
                                    Total Excel: ${reconciliationData.summary.total_paid_excel.toLocaleString()} CLP
                                </Badge>
                            )}
                        </Card.Header>
                        <Card.Body className="bg-light p-4 border-bottom">
                            <Row className="align-items-center g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label className="small fw-bold text-muted text-uppercase" style={{fontSize:'10px'}}>1. Subir Reporte Excel de Booking (xlsx / csv)</Form.Label>
                                        <Form.Control type="file" accept=".xlsx,.xls,.csv" onChange={handleBookingFileChange} />
                                    </Form.Group>
                                </Col>
                                <Col md={3} className="d-flex align-items-end pt-3">
                                    <Button 
                                        variant="dark" 
                                        onClick={handleUploadBookingReport} 
                                        disabled={isReconciling || !bookingFile}
                                        className="w-100"
                                    >
                                        {isReconciling ? <Spinner animation="border" size="sm" className="me-2"/> : <RotateCcw size={14} className="me-2"/>}
                                        {isReconciling ? "Procesando..." : "Conciliar Reporte"}
                                    </Button>
                                </Col>
                                {reconciliationData && reconciliationData.matched.length > 0 && (
                                    <Col md={3} className="d-flex align-items-end pt-3">
                                        <Button 
                                            variant="success" 
                                            onClick={handlePrefacturarConciliados}
                                            disabled={loading}
                                            className="w-100 fw-bold"
                                        >
                                            <CheckCircle size={14} className="me-2"/>
                                            Prefacturar Conciliados ({reconciliationData.matched.length})
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        </Card.Body>

                        {reconciliationData && (
                            <Card.Body className="p-4">
                                {/* Resumen Cards */}
                                <Row className="g-3 mb-4">
                                    {[
                                        { label: "Total Filas Excel", value: reconciliationData.summary.total_excel_rows, variant: "dark" },
                                        { label: "✅ Cuadrados", value: reconciliationData.summary.matched_count, variant: "success" },
                                        { label: "⚠️ Discrepancias", value: reconciliationData.summary.discrepancies_count, variant: "warning" },
                                        { label: "❌ No en EmiGo", value: reconciliationData.summary.missing_in_emigo_count, variant: "danger" },
                                        { label: "🔍 Sin Cobro Booking", value: reconciliationData.summary.missing_in_excel_count, variant: "info" }
                                    ].map((c, idx) => (
                                        <Col key={idx} md={2} className="flex-grow-1">
                                            <div className={`p-3 bg-${c.variant} bg-opacity-10 border border-${c.variant} rounded text-center`}>
                                                <div className="small text-muted fw-bold mb-1" style={{fontSize:'10px'}}>{c.label}</div>
                                                <h4 className={`fw-bold text-${c.variant} mb-0`}>{c.value}</h4>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>

                                {/* Tabs de Navegación del Reporte */}
                                <Tabs 
                                    activeKey={reconActiveSubTab} 
                                    onSelect={k => setReconActiveSubTab(k)} 
                                    className="mb-3 custom-tabs-sub"
                                    style={{fontSize: '12px'}}
                                >
                                    <Tab eventKey="discrepancies" title={`⚠️ Discrepancias de Monto (${reconciliationData.discrepancies.length})`}>
                                        {reconciliationData.discrepancies.length === 0 ? (
                                            <div className="text-center py-5 text-muted">
                                                <CheckCircle size={32} className="text-success mb-2" />
                                                <p className="mb-0">¡No hay discrepancias de tarifas en este reporte!</p>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <span className="small text-muted fw-bold">Viajes con diferencias respecto a la liquidación de Booking:</span>
                                                    <Button 
                                                        variant="warning" 
                                                        size="sm" 
                                                        onClick={() => handleApplyBookingAdjustments(reconciliationData.discrepancies)}
                                                        disabled={isApplyingAdjustments}
                                                        className="fw-bold"
                                                    >
                                                        Ajustar todos en EmiGo en Lote ({reconciliationData.discrepancies.length})
                                                    </Button>
                                                </div>
                                                <Table hover responsive size="sm" className="align-middle" style={{fontSize: '13px'}}>
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th>Reserva ID</th>
                                                            <th>Fecha Viaje</th>
                                                            <th>Estado</th>
                                                            <th className="text-end">Tarifa EmiGo</th>
                                                            <th className="text-end">Pago Booking</th>
                                                            <th className="text-end">Diferencia</th>
                                                            <th className="text-end">Acciones</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {reconciliationData.discrepancies.map(t => (
                                                            <tr key={t.trip_id}>
                                                                <td className="fw-bold">{t.reservation_id}</td>
                                                                <td>{t.scheduled_time ? format(new Date(t.scheduled_time), 'dd/MM/yyyy HH:mm') : 'N/A'}</td>
                                                                <td><Badge bg="secondary">{t.status}</Badge></td>
                                                                <td className="text-end fw-bold">${t.emigo_price.toLocaleString()}</td>
                                                                <td className="text-end text-success fw-bold">${t.booking_paid.toLocaleString()}</td>
                                                                <td className="text-end text-danger fw-bold">-${t.difference.toLocaleString()}</td>
                                                                <td className="text-end">
                                                                    <Button 
                                                                        variant="outline-warning" 
                                                                        size="sm" 
                                                                        onClick={() => handleApplyBookingAdjustments([t])}
                                                                        disabled={isApplyingAdjustments}
                                                                        style={{padding:'2px 8px', fontSize:'11px'}}
                                                                    >
                                                                        Ajustar en EmiGo
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </>
                                        )}
                                    </Tab>

                                    <Tab eventKey="matched" title={`✅ Conciliados Correctos (${reconciliationData.matched.length})`}>
                                        {reconciliationData.matched.length === 0 ? (
                                            <div className="text-center py-5 text-muted">No hay viajes conciliados listos aún.</div>
                                        ) : (
                                            <Table hover responsive size="sm" className="align-middle" style={{fontSize: '13px'}}>
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th>Reserva ID</th>
                                                        <th>Fecha Viaje</th>
                                                        <th>Estado</th>
                                                        <th className="text-end">Monto Conciliado</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reconciliationData.matched.map(t => (
                                                        <tr key={t.trip_id}>
                                                            <td className="fw-bold">{t.reservation_id}</td>
                                                            <td>{t.scheduled_time ? format(new Date(t.scheduled_time), 'dd/MM/yyyy HH:mm') : 'N/A'}</td>
                                                            <td><Badge bg="success">{t.status}</Badge></td>
                                                            <td className="text-end fw-bold text-success">${t.emigo_price.toLocaleString()}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        )}
                                    </Tab>

                                    <Tab eventKey="missing_in_emigo" title={`❌ Faltantes en EmiGo (${reconciliationData.missing_in_emigo.length})`}>
                                        {reconciliationData.missing_in_emigo.length === 0 ? (
                                            <div className="text-center py-5 text-muted">Todas las reservas del Excel existen en EmiGo.</div>
                                        ) : (
                                            <>
                                                <div className="alert alert-danger py-2 small mb-3">
                                                    <AlertTriangle size={14} className="me-2"/>
                                                    Estas reservas vienen informadas en el pago de Booking pero <strong>no se encuentran registradas en EmiGo</strong>.
                                                </div>
                                                <Table hover responsive size="sm" className="align-middle" style={{fontSize: '13px'}}>
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th>Reserva ID</th>
                                                            <th>Fecha Recogida (Excel)</th>
                                                            <th className="text-end">Pago Informado</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {reconciliationData.missing_in_emigo.map((t, idx) => (
                                                            <tr key={idx}>
                                                                <td className="fw-bold text-danger">{t.reservation_id}</td>
                                                                <td>{t.excel_date}</td>
                                                                <td className="text-end fw-bold">${t.booking_paid.toLocaleString()}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </>
                                        )}
                                    </Tab>

                                    <Tab eventKey="missing_in_excel" title={`🔍 No Declaradas por Booking (${reconciliationData.missing_in_excel.length})`}>
                                        {reconciliationData.missing_in_excel.length === 0 ? (
                                            <div className="text-center py-5 text-muted">Todos los viajes completados de EmiGo están cubiertos en el Excel.</div>
                                        ) : (
                                            <>
                                                <div className="alert alert-info py-2 small mb-3">
                                                    <AlertTriangle size={14} className="me-2"/>
                                                    Servicios registrados en EmiGo dentro del período del reporte que <strong>no vinieron informados de pago en esta liquidación</strong>.
                                                </div>
                                                <Table hover responsive size="sm" className="align-middle" style={{fontSize: '13px'}}>
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th>Reserva ID</th>
                                                            <th>Fecha Viaje</th>
                                                            <th>Estado</th>
                                                            <th className="text-end">Monto EmiGo</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {reconciliationData.missing_in_excel.map(t => (
                                                            <tr key={t.trip_id}>
                                                                <td className="fw-bold">{t.reservation_id}</td>
                                                                <td>{t.scheduled_time ? format(new Date(t.scheduled_time), 'dd/MM/yyyy HH:mm') : 'N/A'}</td>
                                                                <td><Badge bg="warning">{t.status}</Badge></td>
                                                                <td className="text-end fw-bold">${t.emigo_price.toLocaleString()}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </>
                                        )}
                                    </Tab>
                                </Tabs>
                            </Card.Body>
                        )}
                    </Card>
                </Tab>
            </Tabs>

            {/* MODAL GESTIÓN DE DOCUMENTOS */}
            <Modal show={showDocManagerModal} onHide={() => setShowDocManagerModal(false)} size="lg">
                <Modal.Header closeButton className="bg-dark text-white">
                    <Modal.Title>Gestión de Documentos: {activeInvoice?.internal_number}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-light">
                    <Row className="mb-4 text-center">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm p-3">
                                <small className="text-muted fw-bold">TOTAL SERVICIOS</small>
                                <h4 className="mb-0 text-dark">
                                    {(!activeInvoice?.trips || activeInvoice.trips.length === 0) ? (
                                        <span className="text-danger fs-6 d-block fw-bold mt-1">No tiene reservas asociadas</span>
                                    ) : (
                                        `$${activeInvoice?.total_amount?.toLocaleString()}`
                                    )}
                                </h4>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm p-3">
                                <small className="text-muted fw-bold">SALDO PENDIENTE</small>
                                <h4 className={`mb-0 ${calculateBalances(activeInvoice).pending > 0 ? 'text-danger' : 'text-success'}`}>
                                    ${calculateBalances(activeInvoice).pending.toLocaleString()}
                                </h4>
                            </Card>
                        </Col>
                    </Row>

                    <Card className="shadow-sm border-0 mb-4">
                        <Card.Header className="bg-white fw-bold py-2">Documentos SII</Card.Header>
                        <Table responsive size="sm" className="mb-0 align-middle">
                            <thead className="bg-light">
                                <tr>
                                    <th>Tipo</th>
                                    <th>Folio</th>
                                    <th>OC / Ref / HES</th>
                                    <th className="text-center">Enviado</th>
                                    <th className="text-end">Monto</th>
                                    <th>Estado</th>
                                    <th>Acción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(!activeInvoice?.documents || activeInvoice.documents.length === 0) ? (
                                    <tr><td colSpan="7" className="text-center py-3 text-muted">No hay documentos registrados.</td></tr>
                                ) : (
                                    activeInvoice.documents.map(doc => (
                                        <tr key={doc.id}>
                                            <td><Badge bg={doc.type === 'FACTURA' ? 'primary' : (doc.type === 'NC' ? 'warning' : 'info')} text={doc.type === 'NC' ? 'dark' : 'white'}>{doc.type}</Badge></td>
                                            <td className="fw-bold">
                                                {editingDocId === doc.id ? (
                                                    <Form.Control 
                                                        size="sm" 
                                                        type="text" 
                                                        placeholder="Folio SII" 
                                                        value={editFolio} 
                                                        onChange={e => setEditFolio(e.target.value)} 
                                                    />
                                                ) : (
                                                    doc.folio ? `#${doc.folio}` : '-'
                                                )}
                                            </td>
                                            <td>
                                                {editingDocId === doc.id ? (
                                                    <Row className="g-1 align-items-center">
                                                        <Col xs={6}>
                                                            <Form.Control 
                                                                size="sm" 
                                                                type="text" 
                                                                placeholder="N° OC" 
                                                                value={editOC} 
                                                                onChange={e => setEditOC(e.target.value)} 
                                                            />
                                                        </Col>
                                                        <Col xs={6}>
                                                             <Form.Control 
                                                                 size="sm" 
                                                                 type="text" 
                                                                 placeholder="N° HES" 
                                                                 value={editHES} 
                                                                 onChange={e => setEditHES(e.target.value)} 
                                                             />
                                                        </Col>
                                                    </Row>
                                                ) : (
                                                    <>
                                                        {doc.oc_number || '-'} 
                                                        {doc.hes_number && ` / HES: ${doc.hes_number}`}
                                                    </>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <Form.Check 
                                                    type="checkbox" 
                                                    checked={doc.is_sent || false} 
                                                    onChange={async () => {
                                                        try {
                                                            await apiClient.put(`transport/invoices/documents/${doc.id}/sent-status`);
                                                            const resInv = await apiClient.get(`transport/invoices/${activeInvoice.id}`);
                                                            const currentInv = resInv.data;
                                                            setActiveInvoice(currentInv);
                                                            setInvoices(prev => prev.map(i => i.id === currentInv.id ? currentInv : i));
                                                        } catch (e) { alert("Error al actualizar estado de envío del documento"); }
                                                    }} 
                                                />
                                            </td>
                                            <td className="text-end fw-bold">
                                                {editingDocId === doc.id ? (
                                                    <Form.Control 
                                                        size="sm" 
                                                        type="number" 
                                                        placeholder="Monto" 
                                                        value={editAmount} 
                                                        onChange={e => setEditAmount(e.target.value)} 
                                                        style={{ width: '110px', marginLeft: 'auto' }}
                                                    />
                                                ) : (
                                                    `$${doc.amount.toLocaleString()}`
                                                )}
                                            </td>
                                            <td>
                                                {editingDocId === doc.id ? (
                                                    <Form.Select 
                                                        size="sm" 
                                                        value={editStatus} 
                                                        onChange={e => setEditStatus(e.target.value)}
                                                        style={{ width: '125px' }}
                                                    >
                                                        <option value="pending">PENDIENTE</option>
                                                        <option value="paid">PAGADA</option>
                                                        <option value="cancelled">{doc.type === 'NC' ? 'APLICADA' : 'ANULADA (NC)'}</option>
                                                    </Form.Select>
                                                ) : doc.type === 'NC' ? (
                                                    <Badge bg="warning" text="dark">
                                                        APLICADA
                                                    </Badge>
                                                ) : doc.status === 'cancelled' ? (
                                                    <Badge bg="danger">
                                                        ANULADA (NC)
                                                    </Badge>
                                                ) : (
                                                    <Badge bg={doc.status === 'paid' ? 'success' : 'secondary'}>
                                                        {doc.status === 'paid' ? 'PAGADA' : 'PENDIENTE'}
                                                    </Badge>
                                                )}
                                            </td>
                                            <td>
                                                {editingDocId === doc.id ? (
                                                    <ButtonGroup size="sm">
                                                        <Button variant="primary" onClick={() => handleSaveEditDoc(doc.id)}><Save size={12}/> Guardar</Button>
                                                        <Button variant="secondary" onClick={() => setEditingDocId(null)}>Cancelar</Button>
                                                    </ButtonGroup>
                                                ) : (
                                                    <ButtonGroup size="sm">
                                                        <Button 
                                                            variant="outline-primary" 
                                                            onClick={() => {
                                                                setEditingDocId(doc.id);
                                                                setEditFolio(doc.folio || '');
                                                                setEditOC(doc.oc_number || '');
                                                                setEditHES(doc.hes_number || '');
                                                                setEditAmount(doc.amount || 0);
                                                                setEditStatus(doc.status || 'pending');
                                                            }}
                                                            title="Editar Documento (OC, HES, Folio, Monto, Estado)"
                                                        >
                                                            <Edit size={12}/> Editar
                                                        </Button>

                                                        {doc.type === 'FACTURA' && doc.status !== 'paid' && doc.status !== 'cancelled' && (
                                                            <Button variant="success" onClick={() => { setActiveDocument(doc); setShowPayModal(true); }} title="Pagar Factura">
                                                                <DollarSign size={12}/> Pagar
                                                            </Button>
                                                        )}

                                                        {doc.status === 'paid' && (
                                                            <Button 
                                                                variant="outline-warning" 
                                                                onClick={() => handleUnpayDoc(doc)} 
                                                                title="Deshacer pago y volver a PENDIENTE"
                                                                disabled={isUnpayingDoc}
                                                            >
                                                                ↩️ Deshacer Pago
                                                            </Button>
                                                        )}

                                                        <Button 
                                                            variant="outline-danger" 
                                                            onClick={() => handleConfirmDeleteDoc(doc)} 
                                                            title="Eliminar documento de la pre-factura"
                                                        >
                                                            <Trash2 size={12}/>
                                                        </Button>
                                                    </ButtonGroup>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </Card>

                    <Card className="border border-primary border-opacity-25 shadow-sm">
                        <Card.Header className="bg-primary bg-opacity-10 text-primary fw-bold py-2"><Plus size={16} className="me-1"/> Nuevo Documento</Card.Header>
                        <Card.Body className="p-3">
                            <Row className="g-2 align-items-end">
                                <Col md={2}>
                                    <Form.Label className="small fw-bold">Tipo</Form.Label>
                                    <Form.Select size="sm" value={newDocType} onChange={e => {
                                        const type = e.target.value;
                                        setNewDocType(type);
                                        if (type === 'OC') setNewDocFolio('');
                                    }}>
                                        <option value="FACTURA">Factura</option>
                                        <option value="NC">Nota de Crédito</option>
                                        <option value="OC">Orden de Compra (OC)</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Label className="small fw-bold">{newDocType === 'OC' ? 'Folio SII (No aplica)' : 'Folio SII'}</Form.Label>
                                    <Form.Control size="sm" value={newDocFolio} onChange={e => setNewDocFolio(e.target.value)} disabled={newDocType === 'OC'} placeholder={newDocType === 'OC' ? 'Deshabilitado' : ''} />
                                </Col>
                                <Col md={2}>
                                    <Form.Label className="small fw-bold">{newDocType === 'OC' ? 'Número OC' : 'OC (Opcional)'}</Form.Label>
                                    <Form.Control size="sm" value={newDocOC} onChange={e => setNewDocOC(e.target.value)} />
                                </Col>
                                <Col md={2}>
                                    <Form.Label className="small fw-bold">HES (Opcional)</Form.Label>
                                    <Form.Control size="sm" value={newDocHES} onChange={e => setNewDocHES(e.target.value)} />
                                </Col>
                                <Col md={2}>
                                     <Form.Label className="small fw-bold">Monto ($)</Form.Label>
                                     <Form.Control 
                                         type="text" 
                                         size="sm" 
                                         value={newDocAmount} 
                                         onChange={e => {
                                             const cleaned = e.target.value.replace(/[^0-9]/g, '');
                                             setNewDocAmount(cleaned);
                                         }} 
                                         placeholder="Ej: 496224"
                                     />
                                 </Col>
                                 <Col md={2}>
                                     <Button variant="primary" size="sm" className="w-100 fw-bold" onClick={async () => {
                                         const cleanAmount = parseInt(newDocAmount, 10);
                                         const isOC = newDocType === 'OC';
                                         if(isOC && !newDocOC) {
                                             return alert("El número de Orden de Compra (OC) es requerido.");
                                         }
                                         if(!isOC && !newDocFolio) {
                                             return alert("El Folio SII es requerido.");
                                         }
                                         if(!newDocAmount || isNaN(cleanAmount) || cleanAmount <= 0) {
                                             return alert("Monto válido y requerido");
                                         }
                                         setLoading(true);
                                         try {
                                             const resDoc = await apiClient.post(`transport/invoices/${activeInvoice.id}/documents`, {
                                                 type: newDocType, 
                                                 folio: isOC ? null : newDocFolio, 
                                                 oc_number: newDocOC, 
                                                 hes_number: newDocHES,
                                                 amount: cleanAmount
                                             });
                                             const inv = resDoc.data;
                                             setActiveInvoice(inv);
                                             setInvoices(prev => prev.map(i => i.id === inv.id ? inv : i));
                                             setNewDocFolio(''); 
                                             setNewDocOC(''); 
                                             setNewDocHES('');
                                             setNewDocAmount(Math.round(calculateBalances(inv).pending));
                                         } catch(e) { 
                                             alert("Error al guardar el documento: " + (e.response?.data?.detail || e.message)); 
                                         } finally { setLoading(false); }
                                     }} disabled={loading}>+ Guardar</Button>
                                 </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={() => setShowDocManagerModal(false)}>Cerrar</Button></Modal.Footer>
            </Modal>

            {/* MODAL PAGO */}
            <Modal show={showPayModal} onHide={() => setShowPayModal(false)}>
                <Modal.Header closeButton className="bg-success text-white"><Modal.Title>Confirmar Pago</Modal.Title></Modal.Header>
                <Modal.Body>
                    <div className="mb-3 p-3 bg-light rounded text-center border">
                        <div className="small text-muted mb-1">{activeDocument ? `Pagando Factura #${activeDocument.folio}` : 'Pago de Servicio'}</div>
                        <h3 className="fw-bold text-success mb-0">
                            ${(activeDocument ? activeDocument.amount : activeTrip ? ((activeTrip.estimated_price || 0) + (parseFloat(activeTrip.waiting_time_amount) || 0) + (activeTrip.extra_services || []).reduce((acc, s) => acc + (parseFloat(s.price) || 0), 0)) : 0).toLocaleString()}
                        </h3>
                    </div>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Método</Form.Label>
                        <Form.Select value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                            <option value="TRANSFER">Transferencia</option>
                            <option value="WEBPAY">Webpay</option>
                            <option value="GETNET">Getnet</option>
                            {!activeTrip && <option value="TRANSBANK">Transbank</option>}
                            <option value="MERCADO_PAGO">Mercado Pago</option>
                            <option value="DLOCAL">DLocal</option>
                            <option value="CASH">Efectivo</option>
                        </Form.Select>
                    </Form.Group>
                    {['WEBPAY', 'GETNET', 'TRANSBANK', 'MERCADO_PAGO', 'DLOCAL'].includes(payMethod) && (
                        <Form.Group className="mb-3 p-3 bg-light border border-warning rounded">
                            <Form.Label className="text-warning small fw-bold">Comisión {payMethod === 'MERCADO_PAGO' ? 'Mercado Pago' : payMethod === 'DLOCAL' ? 'DLocal' : payMethod === 'TRANSBANK' ? 'Transbank' : payMethod} ($)</Form.Label>
                            <Form.Control type="number" size="sm" className="mb-3" value={webpayCommission} onChange={e => setWebpayCommission(e.target.value)} />
                            {['WEBPAY', 'GETNET', 'TRANSBANK', 'MERCADO_PAGO', 'DLOCAL'].includes(payMethod) && (
                                <Form.Check 
                                    type="checkbox"
                                    id="pending-commission-checkbox"
                                    label={<span className="small fw-bold text-dark">Pendiente de Comisión (Depositar en 2 días hábiles)</span>}
                                    checked={pendingCommission}
                                    onChange={e => setPendingCommission(e.target.checked)}
                                />
                            )}
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer><Button variant="success" onClick={handleMarkPaid} disabled={loading}>{loading ? '...' : 'Confirmar Pago'}</Button></Modal.Footer>
            </Modal>

            {/* OTROS MODALES (Pre-Invoice, Bulk, Delete) */}
            <Modal show={showPreInvoiceModal} onHide={() => setShowPreInvoiceModal(false)}>
                <Modal.Header closeButton className="bg-success text-white"><Modal.Title>Pre-Facturar</Modal.Title></Modal.Header>
                <Modal.Body>
                    <p>Se generará un cobro por {selectedTripIds.length} servicios.</p>
                    <h4 className="text-primary">
                        ${trips.filter(t => selectedTripIds.includes(t.id)).reduce((s,t) => s + (t.estimated_price || 0) + (parseFloat(t.waiting_time_amount) || 0) + (t.extra_services || []).reduce((acc, srv) => acc + (parseFloat(srv.price) || 0), 0), 0).toLocaleString()}
                    </h4>
                </Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={() => setShowPreInvoiceModal(false)}>Cancelar</Button><Button variant="success" onClick={handleCreatePreInvoice}>Confirmar</Button></Modal.Footer>
            </Modal>

            <Modal show={showBulkModal} onHide={() => setShowBulkModal(false)}>
                <Modal.Header closeButton className="bg-success text-white"><Modal.Title>Facturar Lote</Modal.Title></Modal.Header>
                <Modal.Body><p>Se generarán pre-facturas para los clientes seleccionados.</p></Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={() => setShowBulkModal(false)}>Cancelar</Button><Button variant="success" onClick={handleBulkGenerate}>Confirmar Lote</Button></Modal.Footer>
            </Modal>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton className="bg-danger text-white"><Modal.Title>Anular Pre-Factura</Modal.Title></Modal.Header>
                <Modal.Body className="text-center"><AlertTriangle size={48} className="text-danger mb-2"/><h5>¿Anular {activeInvoice?.internal_number}?</h5></Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={() => setShowDeleteModal(false)}>No</Button><Button variant="danger" onClick={handleDeleteInvoice}>Sí, Anular</Button></Modal.Footer>
            </Modal>

            {/* MODAL CONFIRMACION ELIMINAR DOCUMENTO SII/OC */}
            <Modal show={showDeleteDocModal} onHide={() => !isDeletingDoc && setShowDeleteDocModal(false)} centered>
                <Modal.Header closeButton={!isDeletingDoc} className="bg-danger text-white">
                    <Modal.Title className="d-flex align-items-center">
                        <AlertTriangle size={20} className="me-2" />
                        Eliminar Documento de Pre-Factura
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    <AlertTriangle size={48} className="text-danger mb-3" />
                    <h5>¿Estás seguro de eliminar este documento?</h5>
                    {docToDelete && (
                        <div className="p-3 bg-light rounded text-start mt-3 border">
                            <div><strong>Tipo:</strong> {docToDelete.type}</div>
                            {docToDelete.folio && <div><strong>Folio:</strong> #{docToDelete.folio}</div>}
                            {docToDelete.oc_number && <div><strong>OC:</strong> {docToDelete.oc_number}</div>}
                            <div><strong>Monto:</strong> ${Number(docToDelete.amount || 0).toLocaleString()}</div>
                            <div><strong>Estado:</strong> <Badge bg={docToDelete.status === 'paid' ? 'success' : 'warning'}>{docToDelete.status === 'paid' ? 'Pagada' : 'Pendiente'}</Badge></div>
                        </div>
                    )}
                    <p className="text-muted small mt-3 mb-0">
                        Esta acción removerá el documento de la pre-factura, ajustará el flujo de caja y recalculará automáticamente el saldo pendiente de la pre-factura.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteDocModal(false)} disabled={isDeletingDoc}>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={executeDeleteDoc} disabled={isDeletingDoc}>
                        {isDeletingDoc ? (
                            <>
                                <Spinner size="sm" animation="border" className="me-1" />
                                Eliminando...
                            </>
                        ) : (
                            'Sí, Eliminar Documento'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* MODAL HISTORIAL DE LOGS DE AUDITORÍA */}
            <Modal show={showLogsModal} onHide={() => setShowLogsModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-dark text-white">
                    <Modal.Title className="d-flex align-items-center">
                        <History size={20} className="me-2 text-info" />
                        Historial de Auditoría: {selectedInvoiceForLogs?.internal_number}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-light p-4" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {loadingLogs ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2 text-muted small">Cargando historial de transacciones...</p>
                        </div>
                    ) : invoiceLogs.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <AlertTriangle size={32} className="text-warning mb-2" />
                            <p className="mb-0">No se encontraron registros de auditoría para esta pre-factura.</p>
                        </div>
                    ) : (
                        <div className="timeline-container">
                            {invoiceLogs.map((log) => {
                                // Formatear fecha
                                let formattedDate = '';
                                try {
                                    formattedDate = format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm:ss');
                                } catch (e) {
                                    formattedDate = log.timestamp;
                                }

                                // Definir estilo/icono por acción
                                let actionBadgeColor = 'secondary';
                                let borderLeftColor = '#6c757d';
                                if (log.action.toLowerCase().includes('crea')) {
                                    actionBadgeColor = 'success';
                                    borderLeftColor = '#198754';
                                } else if (log.action.toLowerCase().includes('documento') || log.action.toLowerCase().includes('folio')) {
                                    actionBadgeColor = 'primary';
                                    borderLeftColor = '#0d6efd';
                                } else if (log.action.toLowerCase().includes('pago') || log.action.toLowerCase().includes('pagada')) {
                                    actionBadgeColor = 'info';
                                    borderLeftColor = '#0dcaf0';
                                } else if (log.action.toLowerCase().includes('anula') || log.action.toLowerCase().includes('elimina')) {
                                    actionBadgeColor = 'danger';
                                    borderLeftColor = '#dc3545';
                                } else if (log.action.toLowerCase().includes('libera') || log.action.toLowerCase().includes('remueve')) {
                                    actionBadgeColor = 'warning';
                                    borderLeftColor = '#ffc107';
                                }

                                return (
                                    <Card 
                                        key={log.id} 
                                        className="mb-3 border-0 shadow-sm overflow-hidden" 
                                        style={{ borderLeft: `5px solid ${borderLeftColor}` }}
                                    >
                                        <Card.Body className="p-3">
                                            <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                                                <div>
                                                    <Badge bg={actionBadgeColor} className="me-2 text-uppercase" style={{ fontSize: '10px' }}>
                                                        {log.action}
                                                    </Badge>
                                                    <span className="small text-muted">
                                                        <Clock size={12} className="me-1 align-text-top" />
                                                        {formattedDate}
                                                    </span>
                                                </div>
                                                <Badge bg="dark" className="fw-normal small d-flex align-items-center">
                                                    <User size={10} className="me-1" />
                                                    {log.user_email}
                                                </Badge>
                                            </div>
                                            {log.details && (
                                                <div className="bg-white p-2 rounded border-start border-3 small text-secondary font-monospace" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '11.5px', borderLeftColor: borderLeftColor }}>
                                                    {log.details}
                                                </div>
                                            )}
                                        </Card.Body>
                                    </Card>
                                );
                            })}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="bg-white">
                    <Button variant="secondary" onClick={() => setShowLogsModal(false)}>Cerrar</Button>
                </Modal.Footer>
            </Modal>

            {/* Modal para Ingresar Orden de Compra (OC) a Servicios Futuros */}
            <Modal show={showAssignOCModal} onHide={() => setShowAssignOCModal(false)} centered>
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title className="h6 mb-0">📋 Ingresar Orden de Compra (OC)</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-info py-2 small mb-3">
                        Se asignará el número de OC a los <strong>{selectedTripIds.length} viaje(s)</strong> seleccionados y se registrará la transacción por cobrar en el <strong>Flujo de Caja</strong>.
                    </div>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Número de Orden de Compra (OC) <span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                            type="text" 
                            placeholder="Ej: OC-99482" 
                            value={assignOCNumber} 
                            onChange={e => setAssignOCNumber(e.target.value)} 
                            autoFocus 
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Notas u Observaciones (Opcional)</Form.Label>
                        <Form.Control 
                            as="textarea" 
                            rows={2} 
                            placeholder="Ej: Factura pendiente de emisión en SII..." 
                            value={assignOCNotes} 
                            onChange={e => setAssignOCNotes(e.target.value)} 
                        />
                    </Form.Group>
                    <div className="p-2 bg-light rounded border text-end">
                        <span className="small text-muted me-2">Monto Total Estimado:</span>
                        <strong className="text-primary h6 mb-0">
                            ${Math.round(selectedTripIds.reduce((acc, id) => {
                                const t = trips.find(tr => tr.id === id);
                                if (!t) return acc;
                                const waitAmount = parseFloat(t.waiting_time_amount) || 0;
                                const extras = (t.extra_services || []).reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);
                                return acc + (parseFloat(t.estimated_price) || 0) + waitAmount + extras;
                            }, 0)).toLocaleString()} CLP
                        </strong>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowAssignOCModal(false)}>Cancelar</Button>
                    <Button 
                        variant="primary" 
                        size="sm" 
                        className="fw-bold" 
                        disabled={!assignOCNumber.trim() || submittingOC}
                        onClick={handleAssignOC}
                    >
                        {submittingOC ? <Spinner animation="border" size="sm" /> : "✓ Confirmar e Ingresar OC"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Gestor de Órdenes de Compra (OCs) Anticipadas */}
            <Modal show={showOCManagerModal} onHide={() => setShowOCManagerModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-dark text-white py-2">
                    <Modal.Title className="h6 mb-0">📋 Órdenes de Compra (OC) Registradas</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <small className="text-muted">Órdenes de Compra registradas anticipadamente.</small>
                        <Button variant="outline-secondary" size="sm" onClick={loadOCSummary}>
                            <RefreshCw size={13} className="me-1"/> Actualizar
                        </Button>
                    </div>

                    {loadingOCSummary ? (
                        <div className="text-center py-5"><Spinner animation="border" variant="primary" size="sm"/> Cargando OCs...</div>
                    ) : ocSummaryList.length === 0 ? (
                        <div className="alert alert-light text-center py-4 border text-muted">
                            No se registran Órdenes de Compra anticipadas.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover size="sm" className="align-middle border mb-0" style={{fontSize: '12px'}}>
                                <thead className="bg-light">
                                    <tr>
                                        <th style={{width: '30px'}}></th>
                                        <th>N° Orden de Compra</th>
                                        <th>Empresa Cliente</th>
                                        <th className="text-center">N° Servicios</th>
                                        <th className="text-end">Monto Total</th>
                                        <th className="text-center">Estado Factura</th>
                                        <th className="text-end">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ocSummaryList.map(item => {
                                        const isExpanded = expandedOCs.includes(item.oc_number);
                                        return (
                                            <React.Fragment key={item.oc_number}>
                                                <tr>
                                                    <td>
                                                        <Button 
                                                            variant="link" 
                                                            size="sm" 
                                                            className="p-0 text-decoration-none text-dark"
                                                            onClick={() => setExpandedOCs(prev => isExpanded ? prev.filter(o => o !== item.oc_number) : [...prev, item.oc_number])}
                                                        >
                                                            {isExpanded ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
                                                        </Button>
                                                    </td>
                                                    <td className="fw-bold text-primary">📋 #{item.oc_number}</td>
                                                    <td className="fw-semibold">{item.client_name}</td>
                                                    <td className="text-center"><Badge bg="secondary">{item.trip_count} viajes</Badge></td>
                                                    <td className="text-end fw-bold">${Math.round(item.total_amount).toLocaleString()}</td>
                                                    <td className="text-center">
                                                        {item.has_invoice ? (
                                                            <Badge bg="success">📄 Factura #{item.invoice_number}</Badge>
                                                        ) : (
                                                            <Badge bg="warning" text="dark">Pendiente Factura</Badge>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        <div className="d-flex justify-content-end gap-1">
                                                            <Button 
                                                                variant="outline-primary" 
                                                                size="sm" 
                                                                className="py-0 px-2 fw-bold"
                                                                style={{fontSize: '11px'}}
                                                                onClick={() => {
                                                                    setSelectedOCForInvoice(item);
                                                                    setAttachFolio(item.invoice_number || '');
                                                                    setShowAttachInvoiceModal(true);
                                                                }}
                                                            >
                                                                📄 {item.has_invoice ? 'Editar Factura' : 'Asociar Factura'}
                                                            </Button>
                                                            <Button 
                                                                variant="outline-danger" 
                                                                size="sm" 
                                                                className="py-0 px-2 fw-bold"
                                                                style={{fontSize: '11px'}}
                                                                title="Eliminar esta Orden de Compra y desvincular sus reservas"
                                                                onClick={() => handleDeleteOC(item.oc_number)}
                                                            >
                                                                <Trash2 size={12} className="me-1"/> Eliminar
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="bg-light">
                                                        <td colSpan="7" className="p-3">
                                                            <div className="fw-bold small text-muted mb-2">Servicios contenidos en la OC #{item.oc_number}:</div>
                                                            <Table size="sm" bordered className="bg-white mb-0" style={{fontSize: '11px'}}>
                                                                <thead className="bg-white">
                                                                    <tr>
                                                                        <th>ID Viaje</th>
                                                                        <th>Fecha / Hora</th>
                                                                        <th>Pasajero</th>
                                                                        <th>Origen ➔ Destino</th>
                                                                        <th>Estado Viaje</th>
                                                                        <th className="text-end">Monto</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {item.trips.map(tr => (
                                                                        <tr key={tr.id}>
                                                                            <td className="fw-bold">#{tr.id}</td>
                                                                            <td>{tr.scheduled_time ? format(new Date(tr.scheduled_time), 'dd/MM/yyyy HH:mm') : '-'}</td>
                                                                            <td>{tr.passenger_name}</td>
                                                                            <td className="small text-muted">{tr.pickup_address} ➔ {tr.dropoff_address}</td>
                                                                            <td><Badge bg="outline-secondary" className="border text-dark">{tr.status}</Badge></td>
                                                                            <td className="text-end fw-bold">${Math.round(tr.amount).toLocaleString()}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </Table>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="py-2">
                    <Button variant="secondary" size="sm" onClick={() => setShowOCManagerModal(false)}>Cerrar</Button>
                </Modal.Footer>
            </Modal>

            {/* Modal para Asociar Factura SII a OC */}
            <Modal show={showAttachInvoiceModal} onHide={() => setShowAttachInvoiceModal(false)} centered>
                <Modal.Header closeButton className="bg-success text-white py-2">
                    <Modal.Title className="h6 mb-0">📄 Asociar Factura SII a OC #{selectedOCForInvoice?.oc_number}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="alert alert-success py-2 small mb-3">
                        Al ingresar la Factura emitida en SII.cl, la transacción en el <strong>Flujo de Caja</strong> se actualizará a la Factura N° {attachFolio || '...'} manteniendo el ingreso registrado.
                    </div>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">N° de Folio de Factura SII <span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                            type="text" 
                            placeholder="Ej: 1420" 
                            value={attachFolio} 
                            onChange={e => setAttachFolio(e.target.value)} 
                            autoFocus 
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Fecha de Emisión de Factura</Form.Label>
                        <Form.Control 
                            type="date" 
                            value={attachDate} 
                            onChange={e => setAttachDate(e.target.value)} 
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="py-2">
                    <Button variant="secondary" size="sm" onClick={() => setShowAttachInvoiceModal(false)}>Cancelar</Button>
                    <Button 
                        variant="success" 
                        size="sm" 
                        className="fw-bold" 
                        disabled={!attachFolio.trim() || submittingAttachInvoice}
                        onClick={handleAttachInvoiceToOC}
                    >
                        {submittingAttachInvoice ? <Spinner animation="border" size="sm" /> : "✓ Guardar Factura SII"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ClientBillingView;
